import React, { useState, useEffect, useMemo, useRef, useCallback, useId } from 'react';
import { TrendingUp, TrendingDown, Crown, Check, X, DollarSign, PieChart, LineChart, BarChart3, ArrowUpDown, Target, Flame, CreditCard, Flag, Scale, GitCompareArrows, BarChart2, RotateCcw } from 'lucide-react';
import { Card, CardHeader, CollapsibleContent } from '../shared';
import { Modal, ModalContent } from '../shared/Modal';
import { UpgradeChartsCTA } from '../modals/UpgradeModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useFinancialCalculations } from '../../hooks/useFinancialCalculations';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { formatCurrency, formatCompactCurrency, formatPercentage } from '../../utils/formatters';
import {
  calculateReturn,
  calculateSMA,
  calculateEMA,
  calculateRSI,
  getSharedChartScale,
  valuesToPolylinePoints,
  valuesToAreaPath,
  valuesToCloudPath,
  valueToY,
  indexToX,
  ChartScale,
  ChartInterval,
  resampleSnapshots,
} from '../../utils/calculations';
import { ChartTab, Investment, CryptoHolding, NetWorthSnapshot, Retirement401k } from '../../types';
import { HISTORY_LINE_COLORS, CHART_COLORS } from '../../config/constants';

interface ChartsWidgetProps {
  onUpgradeClick: () => void;
  variant?: 'card' | 'page';
  defaultTab?: ChartTab;
  onDefaultTabConsumed?: () => void;
  /** Render only the expanded chart dialog for defaultTab, over whatever page is showing. */
  modalOnly?: boolean;
  onClose?: () => void;
}

export const ChartsWidget: React.FC<ChartsWidgetProps> = ({ onUpgradeClick, variant = 'card', defaultTab, onDefaultTabConsumed, modalOnly = false, onClose }) => {
  const { darkMode, privacyMode } = useTheme();
  const { paymentTier } = useAuth();
  const {
    totalAssets, totalLiabilities, netWorth, totalBanking, totalInvestmentValue, totalCrypto, total401k,
    totalCreditUsed, totalCreditLimit, getVisibleNetWorthHistory, investments, cryptoHoldings, retirement401k,
    totalBudgeted, totalSpent, totalGoalsSaved, totalGoalsTarget,
  } = useFinancialData();
  // Free sees the last 12 months; Pro sees everything. Truncation is
  // display-only — the sheet keeps every snapshot.
  const netWorthHistory = useMemo(
    () => getVisibleNetWorthHistory(paymentTier),
    [getVisibleNetWorthHistory, paymentTier]
  );
  const {
    visibleInvestments, chartData, assetDistribution,
    creditCardUtilization, budgetUtilization, goalProgress,
  } = useFinancialCalculations();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('charts');
  const [activeTab, setActiveTab] = useState<ChartTab>(defaultTab || 'networth');
  const [expandedChart, setExpandedChart] = useState<ChartTab | null>(defaultTab || null);

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
      setExpandedChart(defaultTab);
      onDefaultTabConsumed?.();
    }
  }, [defaultTab, onDefaultTabConsumed]);

  const isPro = paymentTier === 'pro';

  // KPI computations for card previews
  const { totalInvestmentGain, totalReturnPct } = useMemo(() => {
    let totalCost = 0;
    let totalCurrent = 0;
    investments.forEach(inv => {
      const cost = (inv.shares || 0) * (inv.costBasis || 0);
      totalCost += cost;
      totalCurrent += inv.totalValue || 0;
    });
    cryptoHoldings.forEach(h => {
      const cost = (h.amount || 0) * (h.costBasis || 0);
      totalCost += cost;
      totalCurrent += h.totalValue || 0;
    });
    const gain = totalCurrent - totalCost;
    const pct = totalCost > 0 ? (gain / totalCost) * 100 : 0;
    return { totalInvestmentGain: gain, totalReturnPct: pct };
  }, [investments, cryptoHoldings]);

  const nwChange = useMemo(() => {
    if (netWorthHistory.length < 2) return null;
    const last = netWorthHistory[netWorthHistory.length - 1].netWorth;
    const prev = netWorthHistory[netWorthHistory.length - 2].netWorth;
    return last - prev;
  }, [netWorthHistory]);

  const overallCreditUtil = useMemo(() => {
    return totalCreditLimit > 0 ? (totalCreditUsed / totalCreditLimit) * 100 : 0;
  }, [totalCreditUsed, totalCreditLimit]);

  const goalsOnTrack = useMemo(() => {
    return goalProgress.filter(g => g.onTrack).length;
  }, [goalProgress]);

  const { bestGainer, worstLoser } = useMemo(() => {
    const allSymbols: { symbol: string; pct: number }[] = [];
    investments.forEach(inv => {
      const r = calculateReturn(inv.shares, inv.costBasis, inv.currentPrice);
      if (r.percentage !== 0) allSymbols.push({ symbol: inv.symbol, pct: r.percentage });
    });
    cryptoHoldings.forEach(h => {
      const cost = h.costBasis || 0;
      if (cost > 0 && h.currentPrice > 0) {
        const pct = ((h.currentPrice - cost) / cost) * 100;
        allSymbols.push({ symbol: h.symbol, pct });
      }
    });
    if (allSymbols.length === 0) return { bestGainer: null, worstLoser: null };
    allSymbols.sort((a, b) => b.pct - a.pct);
    return { bestGainer: allSymbols[0], worstLoser: allSymbols[allSymbols.length - 1] };
  }, [investments, cryptoHoldings]);

  const debtRatio = useMemo(() => {
    return totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : 0;
  }, [totalAssets, totalLiabilities]);

  const renderCardPreview = (key: ChartTab) => {
    const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
    const bright = darkMode ? 'text-white' : 'text-slate-900';
    const green = darkMode ? 'text-emerald-400' : 'text-emerald-600';
    const red = darkMode ? 'text-red-400' : 'text-red-600';

    switch (key) {
      case 'networth': {
        const assetPct = totalAssets + totalLiabilities > 0 ? (totalAssets / (totalAssets + totalLiabilities)) * 100 : 50;
        return (
          <div className="mt-2 space-y-1.5">
            <div className={`text-base font-bold ${netWorth >= 0 ? green : red}`}>{formatCurrency(netWorth, privacyMode)}</div>
            <div className={`w-full h-1.5 rounded-full overflow-hidden ${darkMode ? 'bg-white/10' : 'bg-slate-200'}`}>
              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(assetPct, 100)}%` }} />
            </div>
            <div className={`text-[10px] flex justify-between ${muted}`}>
              <span>Assets</span><span>Liabilities</span>
            </div>
          </div>
        );
      }
      case 'returns':
        return (
          <div className="mt-2 space-y-0.5">
            <div className={`text-base font-bold ${totalInvestmentGain >= 0 ? green : red}`}>{formatCurrency(totalInvestmentGain, privacyMode)}</div>
            <div className={`text-xs ${muted}`}>{formatPercentage(totalReturnPct)} overall</div>
          </div>
        );
      case 'allocation': {
        const top3 = [...(chartData || [])].sort((a, b) => b.percentage - a.percentage).slice(0, 3);
        return (
          <div className="mt-2 space-y-1">
            {top3.map(item => (
              <div key={item.name} className="flex items-center gap-1.5">
                <div className="w-full flex-1 h-1 rounded-full overflow-hidden" style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb' }}>
                  <div className="h-full rounded-full" style={{ width: `${item.percentage}%`, backgroundColor: item.color }} />
                </div>
                <span className={`text-[10px] w-16 text-right ${muted}`}>{item.name} {item.percentage.toFixed(0)}%</span>
              </div>
            ))}
          </div>
        );
      }
      case 'history':
        return (
          <div className="mt-2 space-y-0.5">
            {nwChange !== null ? (
              <>
                <div className={`text-base font-bold flex items-center gap-1 ${nwChange >= 0 ? green : red}`}>
                  {nwChange >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {formatCurrency(Math.abs(nwChange), privacyMode)}
                </div>
                <div className={`text-xs ${muted}`}>since last snapshot</div>
              </>
            ) : (
              <div className={`text-xs ${muted}`}>Not enough data yet</div>
            )}
          </div>
        );
      case 'stackedArea': {
        const assetClasses = [
          { label: 'Banking', value: totalBanking },
          { label: 'Investments', value: totalInvestmentValue },
          { label: '401k', value: total401k },
          { label: 'Crypto', value: totalCrypto },
        ].filter(a => a.value > 0).sort((a, b) => b.value - a.value);
        return (
          <div className="mt-2 space-y-0.5">
            {assetClasses.length > 0 ? (
              <div className={`text-xs ${muted}`}>
                {assetClasses.map((a, i) => {
                  const pct = totalAssets > 0 ? (a.value / totalAssets) * 100 : 0;
                  return (i > 0 ? ' · ' : '') + `${a.label} ${pct.toFixed(0)}%`;
                }).join('')}
              </div>
            ) : (
              <div className={`text-xs ${muted}`}>No assets yet</div>
            )}
          </div>
        );
      }
      case 'treemap':
        return (
          <div className="mt-2 space-y-0.5">
            {bestGainer ? (
              <>
                <div className={`text-xs flex items-center gap-1 ${green}`}>
                  <TrendingUp size={10} /> {bestGainer.symbol} {formatPercentage(bestGainer.pct)}
                </div>
                {worstLoser && worstLoser.pct < 0 && (
                  <div className={`text-xs flex items-center gap-1 ${red}`}>
                    <TrendingDown size={10} /> {worstLoser.symbol} {formatPercentage(worstLoser.pct, 1, false)}
                  </div>
                )}
              </>
            ) : (
              <div className={`text-xs ${muted}`}>No holdings data</div>
            )}
          </div>
        );
      case 'budgetDonut': {
        const budgetPct = totalBudgeted > 0 ? (totalSpent / totalBudgeted) * 100 : 0;
        const remaining = totalBudgeted - totalSpent;
        return (
          <div className="mt-2 space-y-0.5">
            <div className={`text-base font-bold ${bright}`}>{formatCurrency(totalSpent, privacyMode)}<span className={`text-xs font-normal ${muted}`}> / {formatCurrency(totalBudgeted, privacyMode)}</span></div>
            <div className={`text-xs ${remaining >= 0 ? green : red}`}>{budgetPct.toFixed(0)}% spent</div>
          </div>
        );
      }
      case 'burndown': {
        const remaining = totalBudgeted - totalSpent;
        return (
          <div className="mt-2 space-y-0.5">
            <div className={`text-base font-bold ${remaining >= 0 ? green : red}`}>{formatCurrency(remaining, privacyMode)}</div>
            <div className={`text-xs ${muted}`}>{remaining >= 0 ? 'remaining' : 'over budget'}</div>
          </div>
        );
      }
      case 'creditGauges':
        return (
          <div className="mt-2 space-y-0.5">
            <div className={`text-base font-bold ${overallCreditUtil > 30 ? (overallCreditUtil > 50 ? red : 'text-yellow-500') : green}`}>{overallCreditUtil.toFixed(0)}%</div>
            <div className={`text-xs ${muted}`}>{formatCurrency(totalCreditUsed, privacyMode)} / {formatCurrency(totalCreditLimit, privacyMode)}</div>
          </div>
        );
      case 'goalsProgress': {
        const goalPct = totalGoalsTarget > 0 ? (totalGoalsSaved / totalGoalsTarget) * 100 : 0;
        return (
          <div className="mt-2 space-y-0.5">
            <div className={`text-base font-bold ${bright}`}>{goalsOnTrack}<span className={`text-xs font-normal ${muted}`}> / {goalProgress.length} on track</span></div>
            <div className={`text-xs ${muted}`}>{goalPct.toFixed(0)}% saved overall</div>
          </div>
        );
      }
      case 'debtRatio': {
        const badgeColor = debtRatio > 50 ? (darkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700')
          : debtRatio > 25 ? (darkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
          : (darkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700');
        return (
          <div className="mt-2 space-y-1">
            <div className={`text-base font-bold ${bright}`}>{debtRatio.toFixed(1)}%</div>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${badgeColor}`}>
              {debtRatio > 50 ? 'High' : debtRatio > 25 ? 'Moderate' : 'Low'}
            </span>
          </div>
        );
      }
      case 'comparison':
        return (
          <div className="mt-2 space-y-0.5">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] ${muted}`}>401k</span>
              <span className={`text-xs font-bold ${bright}`}>{formatCurrency(total401k, privacyMode)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] ${muted}`}>Stocks</span>
              <span className={`text-xs font-bold ${bright}`}>{formatCurrency(totalInvestmentValue, privacyMode)}</span>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const CHART_CARDS: { key: ChartTab; label: string; icon: React.ReactNode; desc: string }[] = [
    { key: 'networth', label: 'Net Worth', icon: <DollarSign size={18} />, desc: 'Assets, liabilities & breakdown' },
    { key: 'returns', label: 'Returns', icon: <TrendingUp size={18} />, desc: 'Investment & crypto gains' },
    { key: 'allocation', label: 'Allocation', icon: <PieChart size={18} />, desc: 'Asset allocation donut' },
    { key: 'history', label: 'Trends', icon: <LineChart size={18} />, desc: 'Net worth over time' },
    { key: 'stackedArea', label: 'Composition', icon: <BarChart3 size={18} />, desc: 'Asset mix over time' },
    { key: 'treemap', label: 'Gain/Loss', icon: <ArrowUpDown size={18} />, desc: 'Per-symbol waterfall' },
    { key: 'budgetDonut', label: 'Budget', icon: <Target size={18} />, desc: 'Spending by category' },
    { key: 'burndown', label: 'Burn-Down', icon: <Flame size={18} />, desc: 'Budget remaining' },
    { key: 'creditGauges', label: 'Credit', icon: <CreditCard size={18} />, desc: 'Card utilization gauges' },
    { key: 'goalsProgress', label: 'Goals', icon: <Flag size={18} />, desc: 'Savings goal progress' },
    { key: 'debtRatio', label: 'Debt Ratio', icon: <Scale size={18} />, desc: 'Debt-to-asset trend' },
    { key: 'comparison', label: '401k vs Stocks', icon: <GitCompareArrows size={18} />, desc: 'Account type comparison' },
  ];

  const renderTabButton = (tab: ChartTab, label: string) => (
    <button
      onClick={() => setActiveTab(tab)}
      className={`px-3 py-1.5 text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 ${
        activeTab === tab
          ? darkMode
            ? 'text-violet-400 border-b-2 border-violet-400'
            : 'text-violet-600 border-b-2 border-violet-600'
          : darkMode
          ? 'text-slate-400 hover:text-slate-300'
          : 'text-slate-600 hover:text-slate-800'
      }`}
    >
      {label}
    </button>
  );

  const isPage = variant === 'page';

  if (!isPro) {
    // Free Tier - Basic Snapshot
    return (
      <Card variant={variant}>
        <CardHeader
          title="Financial Overview"
          icon={<BarChart2 size={20} />}
          action={
            <button
              onClick={onUpgradeClick}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${
                darkMode
                  ? 'bg-violet-600/30 hover:bg-violet-600/40 text-violet-300'
                  : 'bg-violet-100 hover:bg-violet-200 text-violet-700'
              } transition-all`}
            >
              <Crown size={14} className="text-yellow-500" />
              Upgrade for Charts
            </button>
          }
          collapsed={isPage ? false : collapsed}
          onToggleCollapse={isPage ? undefined : () => toggleCollapse('charts')}
          variant={variant}
        />

        <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
        <div className="space-y-4">
          <div
            className={`p-4 rounded-xl ${
              darkMode
                ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30'
                : 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'
            }`}
          >
            <div className={`text-sm font-semibold mb-2 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
              Total Assets
            </div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              {formatCurrency(totalAssets, privacyMode)}
            </div>
          </div>

          <div
            className={`p-4 rounded-xl ${
              darkMode
                ? 'bg-gradient-to-br from-red-500/20 to-pink-500/20 border border-red-500/30'
                : 'bg-gradient-to-br from-red-50 to-pink-50 border border-red-200'
            }`}
          >
            <div className={`text-sm font-semibold mb-2 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
              Total Liabilities
            </div>
            <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              {formatCurrency(totalLiabilities, privacyMode)}
            </div>
          </div>

          <div
            className={`p-4 rounded-xl ${
              darkMode
                ? 'bg-white/[0.04] border border-white/10'
                : 'bg-slate-50 border border-slate-200/60'
            }`}
          >
            <div className={`text-sm font-semibold mb-2 ${darkMode ? 'text-violet-300' : 'text-violet-700'}`}>
              Asset Distribution
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className={`text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Banking</span>
                <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {assetDistribution.banking.toFixed(0)}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Investments</span>
                <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {assetDistribution.investments.toFixed(0)}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>401k Retirement</span>
                <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {assetDistribution.retirement.toFixed(0)}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Crypto</span>
                <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {assetDistribution.crypto.toFixed(0)}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Other Assets</span>
                <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {assetDistribution.other.toFixed(0)}%
                </span>
              </div>
            </div>
          </div>

          <div
            className={`p-4 rounded-xl ${
              darkMode
                ? 'bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30'
                : 'bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200'
            }`}
          >
            <div className={`text-sm font-semibold mb-2 ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
              Historical Trends
            </div>
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Upgrade to Pro to see your net worth trend over time with interactive charts.
            </p>
          </div>

          <UpgradeChartsCTA onClick={onUpgradeClick} />
        </div>
        </CollapsibleContent>
      </Card>
    );
  }

  const renderChartContent = (tab: ChartTab) => {
    switch (tab) {
      case 'history': return <HistoryTab darkMode={darkMode} privacyMode={privacyMode} snapshots={netWorthHistory} currentNetWorth={netWorth} />;
      case 'networth': return <NetWorthTab darkMode={darkMode} privacyMode={privacyMode} netWorth={netWorth} totalAssets={totalAssets} totalLiabilities={totalLiabilities} totalBanking={totalBanking} totalInvestmentValue={totalInvestmentValue} totalCrypto={totalCrypto} total401k={total401k} totalCreditUsed={totalCreditUsed} />;
      case 'returns': return <ReturnsTab darkMode={darkMode} privacyMode={privacyMode} investments={visibleInvestments} cryptoHoldings={cryptoHoldings} />;
      case 'allocation': return <AllocationTab darkMode={darkMode} privacyMode={privacyMode} chartData={chartData} />;
      case 'budgetDonut': return <BudgetDonutTab darkMode={darkMode} privacyMode={privacyMode} budgetUtilization={budgetUtilization} totalSpent={totalSpent} totalBudgeted={totalBudgeted} />;
      case 'creditGauges': return <CreditGaugesTab darkMode={darkMode} privacyMode={privacyMode} creditCardUtilization={creditCardUtilization} />;
      case 'goalsProgress': return <GoalsProgressTab darkMode={darkMode} privacyMode={privacyMode} goalProgress={goalProgress} />;
      case 'stackedArea': return <StackedAreaTab darkMode={darkMode} privacyMode={privacyMode} snapshots={netWorthHistory} />;
      case 'treemap': return <TreemapTab darkMode={darkMode} privacyMode={privacyMode} investments={investments} cryptoHoldings={cryptoHoldings} />;
      case 'debtRatio': return <DebtRatioTab darkMode={darkMode} privacyMode={privacyMode} snapshots={netWorthHistory} totalAssets={totalAssets} totalLiabilities={totalLiabilities} />;
      case 'comparison': return <ComparisonTab darkMode={darkMode} privacyMode={privacyMode} investments={investments} retirement401k={retirement401k} totalInvestmentValue={totalInvestmentValue} total401k={total401k} totalCrypto={totalCrypto} cryptoHoldings={cryptoHoldings} />;
      case 'burndown': return <BurnDownTab darkMode={darkMode} privacyMode={privacyMode} budgetUtilization={budgetUtilization} totalBudgeted={totalBudgeted} totalSpent={totalSpent} />;
      default: return null;
    }
  };

  // Pro Tier — Page variant: grid of cards with expand-to-modal
  if (isPage) {
    const expandedCard = CHART_CARDS.find(c => c.key === expandedChart);
    const closeChart = () => {
      setExpandedChart(null);
      onClose?.();
    };
    return (
      <>
        {!modalOnly && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-fr">
          {CHART_CARDS.map(card => (
            <button
              key={card.key}
              onClick={() => setExpandedChart(card.key)}
              className={`text-left p-4 rounded-xl border transition-all h-full flex flex-col justify-start ${
                darkMode
                  ? 'bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-violet-500/40'
                  : 'bg-white border-slate-200 hover:bg-violet-50/50 hover:border-violet-300'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${
                  darkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-600'
                }`}>{card.icon}</span>
                <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{card.label}</div>
              </div>
              {renderCardPreview(card.key)}
            </button>
          ))}
        </div>}

        <Modal isOpen={expandedChart !== null} onClose={closeChart} maxWidth="4xl">
          <ModalContent>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold flex items-center gap-2.5 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {expandedCard && (
                  <>
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${
                      darkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-600'
                    }`}>{expandedCard.icon}</span>
                    {expandedCard.label}
                  </>
                )}
              </h3>
              <button
                onClick={closeChart}
                aria-label="Close"
                className={`p-1.5 rounded-lg transition-colors ${
                  darkMode ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                }`}
              >
                <X size={18} />
              </button>
            </div>
            {expandedChart && renderChartContent(expandedChart)}
          </ModalContent>
        </Modal>
      </>
    );
  }

  // Pro Tier — Card variant: tab-based layout (unchanged)
  return (
    <Card variant={variant}>
      <CardHeader
        title="Financial Overview"
        icon={<BarChart2 size={20} />}
        collapsed={collapsed}
        onToggleCollapse={() => toggleCollapse('charts')}
        variant={variant}
      />

      <CollapsibleContent collapsed={collapsed} variant={variant}>
      <div className="space-y-4">
        {/* Tab Navigation - Scrollable */}
        <div className="flex gap-1 border-b border-white/10 overflow-x-auto scrollbar-hide flex-nowrap">
          {renderTabButton('networth', 'Net Worth')}
          {renderTabButton('returns', 'Returns')}
          {renderTabButton('allocation', 'Allocation')}
          {renderTabButton('history', 'Trends')}
          {renderTabButton('stackedArea', 'Composition')}
          {renderTabButton('treemap', 'Gain/Loss')}
          {renderTabButton('budgetDonut', 'Budget')}
          {renderTabButton('burndown', 'Burn-Down')}
          {renderTabButton('creditGauges', 'Credit')}
          {renderTabButton('goalsProgress', 'Goals')}
          {renderTabButton('debtRatio', 'Debt Ratio')}
          {renderTabButton('comparison', '401k vs Stocks')}
        </div>

        <div>
          {renderChartContent(activeTab)}
        </div>
      </div>
      </CollapsibleContent>
    </Card>
  );
};

// ─── Existing Sub-components ─────────────────────────────────────────────────

interface NetWorthTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  netWorth: number;
  totalAssets: number;
  totalLiabilities: number;
  totalBanking: number;
  totalInvestmentValue: number;
  totalCrypto: number;
  total401k: number;
  totalCreditUsed: number;
}

const NetWorthTab: React.FC<NetWorthTabProps> = ({
  darkMode,
  privacyMode,
  netWorth,
  totalAssets,
  totalLiabilities,
  totalBanking,
  totalInvestmentValue,
  totalCrypto,
  total401k,
  totalCreditUsed,
}) => {
  const otherAssets = totalAssets - totalBanking - totalInvestmentValue - total401k - totalCrypto;
  const otherLiabilities = totalLiabilities - totalCreditUsed;
  const assetItems = [
    { label: 'Banking', value: totalBanking },
    { label: 'Investments', value: totalInvestmentValue },
    { label: '401k', value: total401k },
    ...(totalCrypto > 0 ? [{ label: 'Crypto', value: totalCrypto }] : []),
    ...(otherAssets > 0 ? [{ label: 'Other Assets', value: otherAssets }] : []),
  ].sort((a, b) => b.value - a.value);
  const liabilityItems = [
    { label: 'Credit Cards', value: totalCreditUsed },
    ...(otherLiabilities > 0 ? [{ label: 'Other', value: otherLiabilities }] : []),
  ].sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6 py-2">
      {/* Hero number */}
      <div className="text-center">
        <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Net Worth</div>
        <div className={`text-3xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
          {formatCurrency(netWorth, privacyMode)}
        </div>
      </div>

      {/* Assets vs Liabilities — inline summary */}
      <div className="flex items-center justify-center gap-6">
        <div className="text-center">
          <div className={`text-lg font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{formatCurrency(totalAssets, privacyMode)}</div>
          <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Assets</div>
        </div>
        <div className={`text-lg ${darkMode ? 'text-slate-600' : 'text-slate-300'}`}>/</div>
        <div className="text-center">
          <div className={`text-lg font-bold ${darkMode ? 'text-red-400' : 'text-red-500'}`}>{formatCurrency(totalLiabilities, privacyMode)}</div>
          <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Liabilities</div>
        </div>
      </div>

      {/* Proportion bar */}
      <div className="flex h-2 rounded-full overflow-hidden">
        <div className={`${darkMode ? 'bg-green-500' : 'bg-green-500'}`} style={{ width: `${totalAssets + totalLiabilities > 0 ? (totalAssets / (totalAssets + totalLiabilities)) * 100 : 50}%` }} />
        <div className={`${darkMode ? 'bg-red-500' : 'bg-red-400'}`} style={{ width: `${totalAssets + totalLiabilities > 0 ? (totalLiabilities / (totalAssets + totalLiabilities)) * 100 : 50}%` }} />
      </div>

      {/* Breakdown — clean rows, no boxes */}
      <div className={`border-t ${darkMode ? 'border-white/10' : 'border-slate-100'} pt-4 space-y-2.5`}>
        {assetItems.map((item, i) => (
          <div key={i} className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{item.label}</span>
            </div>
            <span className={`text-sm font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{formatCurrency(item.value, privacyMode)}</span>
          </div>
        ))}
        {liabilityItems.map((item, i) => (
          <div key={i} className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
              <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{item.label}</span>
            </div>
            <span className={`text-sm font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{formatCurrency(item.value, privacyMode)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface ReturnsTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  investments: Investment[];
  cryptoHoldings: CryptoHolding[];
}

const ReturnsTab: React.FC<ReturnsTabProps> = ({ darkMode, privacyMode, investments, cryptoHoldings }) => {
  if (investments.length === 0 && cryptoHoldings.length === 0) {
    return (
      <div className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        <p className="text-sm">No investments to display</p>
        <p className="text-xs mt-1">Add investments or crypto to see performance</p>
      </div>
    );
  }

  // Aggregate by symbol across all accounts
  const symbolMap = new Map<string, { value: number; totalCost: number; isCrypto: boolean }>();

  investments.forEach(inv => {
    const existing = symbolMap.get(inv.symbol) || { value: 0, totalCost: 0, isCrypto: false };
    existing.value += inv.totalValue;
    existing.totalCost += inv.shares * inv.costBasis;
    symbolMap.set(inv.symbol, existing);
  });

  cryptoHoldings.filter(h => h.totalValue > 0).forEach(h => {
    const existing = symbolMap.get(h.symbol) || { value: 0, totalCost: 0, isCrypto: true };
    existing.value += h.totalValue;
    existing.totalCost += h.amount * h.costBasis;
    existing.isCrypto = true;
    symbolMap.set(h.symbol, existing);
  });

  const aggregated = Array.from(symbolMap.entries()).map(([symbol, data]) => {
    const gain = data.value - data.totalCost;
    const pct = data.totalCost > 0 ? (gain / data.totalCost) * 100 : 0;
    return { symbol, value: data.value, gain, pct, isCrypto: data.isCrypto };
  }).sort((a, b) => b.pct - a.pct);

  const totalGain = aggregated.reduce((sum, h) => sum + h.gain, 0);

  return (
    <div className="space-y-4 py-2">
      <div className="text-center">
        <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Total Return</div>
        <div className={`text-3xl font-bold ${totalGain >= 0 ? 'text-green-500' : 'text-red-500'}`}>
          {totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain, privacyMode)}
        </div>
      </div>

      <div className={`border-t ${darkMode ? 'border-white/10' : 'border-slate-100'} pt-3 space-y-2`}>
        {aggregated.map((item, idx) => {
          const isPositive = item.pct >= 0;
          return (
            <div key={idx} className={`flex items-center justify-between py-1.5 ${idx > 0 ? `border-t ${darkMode ? 'border-white/5' : 'border-slate-50'}` : ''}`}>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-semibold ${item.isCrypto ? (darkMode ? 'text-amber-400' : 'text-amber-600') : (darkMode ? 'text-slate-200' : 'text-slate-800')}`}>{item.symbol}</span>
                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{formatCurrency(item.value, privacyMode)}</span>
              </div>
              <div className="flex items-center">
                <span className={`text-sm font-medium text-right w-28 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {isPositive ? '+' : ''}{formatCurrency(item.gain, privacyMode)}
                </span>
                <span className={`text-sm font-bold text-right w-20 ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                  {item.pct > 0 ? '+' : ''}{item.pct.toFixed(1)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface AllocationTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  chartData: Array<{
    name: string;
    value: number;
    type: string;
    color: string;
    percentage: number;
    startPercentage: number;
  }>;
}

// History/Trends Tab — MA cloud, RSI pane, zoomable shared-scale chart
type MaPeriod = 20 | 50 | 200;
const INTERVALS: Array<{ key: ChartInterval; label: string; suffix: string; unit: string }> = [
  { key: 'day', label: 'Day', suffix: 'D', unit: 'days' },
  { key: 'week', label: 'Week', suffix: 'W', unit: 'weeks' },
  { key: 'month', label: 'Month', suffix: 'M', unit: 'months' },
];
type ScaleMode = 'full' | 'fit';
type XWindow = { start: number; end: number };

const TRENDS_VIEWBOX = { width: 560, height: 280 };
const TRENDS_PADDING = { top: 16, right: 56, bottom: 28, left: 52 };
const RSI_VIEWBOX = { width: 560, height: 110 };
const RSI_PADDING = { top: 10, right: 56, bottom: 22, left: 52 };
const RSI_PERIOD = 14;
const RSI_OVERBOUGHT = 70;
const RSI_OVERSOLD = 30;
const MIN_X_SPAN = 7;
/** Max Y zoom-out vs auto scale — stops trackpad scrolls from rocketing into trillions. */
const MAX_Y_RANGE_MULT = 3;
/** Min Y zoom-in vs auto scale — still enough for trough depth. */
const MIN_Y_RANGE_MULT = 0.03;
const MIN_Y_RANGE_ABS = 500;

const wheelZoomFactor = (deltaY: number): number => {
  // Trackpads fire many tiny events; clamp so one flick can't explode the scale.
  const clamped = Math.max(-48, Math.min(48, deltaY));
  return Math.exp(clamped * 0.0025);
};

/** Keep Y zoom/pan from sliding the series off-screen. Always overlaps data. */
const clampYScale = (
  minVal: number,
  maxVal: number,
  autoScale: ChartScale,
  floorAtZero: boolean
): ChartScale => {
  const dataMin = autoScale.minVal;
  const dataMax = autoScale.maxVal;
  const dataMid = (dataMin + dataMax) / 2;
  const dataRange = Math.max(autoScale.range, MIN_Y_RANGE_ABS);
  const maxRange = dataRange * MAX_Y_RANGE_MULT;
  const minRange = Math.max(dataRange * MIN_Y_RANGE_MULT, MIN_Y_RANGE_ABS);

  let range = maxVal - minVal;
  if (!Number.isFinite(range) || range <= 0) {
    return { ...autoScale };
  }

  // Grow/shrink about the requested midpoint — never re-anchor mid-gesture.
  let mid = (minVal + maxVal) / 2;
  if (range > maxRange) range = maxRange;
  else if (range < minRange) range = minRange;

  // Zoomed out past the data frame → snap to auto scale (full view).
  if (range >= autoScale.range) {
    return { ...autoScale };
  }

  minVal = mid - range / 2;
  maxVal = mid + range / 2;

  // Keep the window inside the data frame so the line stays on screen.
  if (minVal < dataMin) {
    minVal = dataMin;
    maxVal = dataMin + range;
  }
  if (maxVal > dataMax) {
    maxVal = dataMax;
    minVal = dataMax - range;
  }
  if (minVal < dataMin) {
    minVal = dataMin;
    maxVal = dataMax;
    mid = dataMid;
    range = maxVal - minVal;
  }

  if (floorAtZero && minVal < 0 && dataMin >= 0) {
    maxVal -= minVal;
    minVal = 0;
  }

  return { minVal, maxVal, range: maxVal - minVal || 1 };
};

interface HistoryTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  snapshots: NetWorthSnapshot[];
  currentNetWorth: number;
}

/**
 * Horizontal and vertical gap between the first two active pointers, or null
 * with fewer than two.
 */
const pinchSpread = (
  pointers: Map<number, { x: number; y: number }>
): { dx: number; dy: number } | null => {
  const pts = Array.from(pointers.values());
  if (pts.length < 2) return null;
  return { dx: Math.abs(pts[0].x - pts[1].x), dy: Math.abs(pts[0].y - pts[1].y) };
};

// Pinch tuning. Fingers must move this far before anything zooms, so resting
// jitter does nothing. The exponent softens the response: spreading the
// fingers to double their gap zooms about 1.5x instead of 2x.
const PINCH_DEAD_ZONE_PX = 12;
const PINCH_DAMPING = 0.6;
// Floor on the measured gap, so fingers that start close together along the
// zoom axis can't produce a huge ratio from a few pixels of movement.
const PINCH_MIN_GAP_PX = 40;

const clampWindow = (start: number, end: number, maxIndex: number): XWindow => {
  const lo = Math.max(0, Math.min(start, maxIndex));
  const hi = Math.max(0, Math.min(end, maxIndex));
  if (hi - lo < Math.min(MIN_X_SPAN, maxIndex)) {
    const span = Math.min(MIN_X_SPAN, maxIndex);
    const mid = (lo + hi) / 2;
    let nextStart = Math.round(mid - span / 2);
    let nextEnd = nextStart + span;
    if (nextStart < 0) {
      nextStart = 0;
      nextEnd = span;
    }
    if (nextEnd > maxIndex) {
      nextEnd = maxIndex;
      nextStart = Math.max(0, maxIndex - span);
    }
    return { start: nextStart, end: nextEnd };
  }
  return { start: lo, end: hi };
};

const HistoryTab: React.FC<HistoryTabProps> = ({ darkMode, privacyMode, snapshots, currentNetWorth }) => {
  const [maPeriod, setMaPeriod] = useState<MaPeriod>(200);
  const [interval, setChartInterval] = useState<ChartInterval>('day');
  // Fit + NW-only matches the dashboard sparkline depth (min/max of the line).
  // Full ($0 floor) with assets/liabilities flattens the same series.
  const [scaleMode, setScaleMode] = useState<ScaleMode>('fit');
  const [xWindow, setXWindow] = useState<XWindow | null>(null);
  const [yOverride, setYOverride] = useState<ChartScale | null>(null);
  const [showNetWorth, setShowNetWorth] = useState(true);
  const [showAssets, setShowAssets] = useState(false);
  const [showLiabilities, setShowLiabilities] = useState(false);
  const [showCloud, setShowCloud] = useState(true);
  const [showRsi, setShowRsi] = useState(true);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const rsiSvgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{
    kind: 'pan-xy' | 'pan-y' | null;
    pointerId: number | null;
    lastClientX: number;
    lastClientY: number;
    xAcc: number;
  }>({ kind: null, pointerId: null, lastClientX: 0, lastClientY: 0, xAcc: 0 });
  const areaFillId = useId().replace(/:/g, '');
  // Every finger currently on the chart, by pointerId. Needed because each
  // finger is its own pointer: with one shared drag record, a two-finger pinch
  // made every move compute its delta against the OTHER finger's position,
  // which flung the chart around wildly.
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  // Pinch is measured against where it started, not accumulated per event, so
  // the scale can never run away no matter how many move events fire. The
  // axis is locked at the start: fingers side by side stretch time (X),
  // fingers one above the other stretch dollars (Y).
  const pinchRef = useRef<{
    axis: 'x' | 'y';
    startGap: number;
    startWindow: XWindow;
    startY: ChartScale;
  } | null>(null);

  const daily = useMemo(
    () => [...snapshots].sort((a, b) => a.date.localeCompare(b.date)),
    [snapshots]
  );
  // One point per day, week or month; the line, cloud and RSI all use it.
  const sorted = useMemo(() => resampleSnapshots(daily, interval), [daily, interval]);
  const intervalInfo = INTERVALS.find(i => i.key === interval)!;
  const intervalPointCounts = useMemo(
    () => Object.fromEntries(INTERVALS.map(i => [i.key, resampleSnapshots(daily, i.key).length])) as Record<ChartInterval, number>,
    [daily]
  );
  const maxIndex = Math.max(0, sorted.length - 1);

  const fullSeries = useMemo(() => {
    const netWorth = sorted.map(s => s.netWorth || 0);
    const assets = sorted.map(s => s.totalAssets || 0);
    // Match how net worth is computed: liabilities sheet + credit card balances
    const liabilities = sorted.map(s => (s.totalLiabilities || 0) + (s.creditCards || 0));
    const sma = calculateSMA(netWorth, maPeriod);
    const ema = calculateEMA(netWorth, maPeriod);
    const rsi = calculateRSI(netWorth, RSI_PERIOD);
    return { netWorth, assets, liabilities, sma, ema, rsi };
  }, [sorted, maPeriod]);

  const viewWindow = useMemo(
    () => xWindow ?? { start: 0, end: maxIndex },
    [xWindow, maxIndex]
  );

  const series = useMemo(() => {
    const slice = <T,>(arr: T[]) => arr.slice(viewWindow.start, viewWindow.end + 1);
    return {
      netWorth: slice(fullSeries.netWorth),
      assets: slice(fullSeries.assets),
      liabilities: slice(fullSeries.liabilities),
      sma: slice(fullSeries.sma),
      ema: slice(fullSeries.ema),
      rsi: slice(fullSeries.rsi),
    };
  }, [fullSeries, viewWindow]);

  const visibleSorted = useMemo(
    () => sorted.slice(viewWindow.start, viewWindow.end + 1),
    [sorted, viewWindow]
  );

  const { width: viewBoxWidth, height: viewBoxHeight } = TRENDS_VIEWBOX;
  const padding = TRENDS_PADDING;

  const autoScale = useMemo(() => {
    const visible: Array<Array<number | null>> = [];
    if (showNetWorth) visible.push(series.netWorth);
    if (showAssets) visible.push(series.assets);
    if (showLiabilities) visible.push(series.liabilities);
    if (showCloud) {
      visible.push(series.sma);
      visible.push(series.ema);
    }
    // Fit uses a tight pad so troughs/peaks fill the pane like the widget sparkline.
    return getSharedChartScale(visible.length ? visible : [series.netWorth], {
      floorAtZero: scaleMode === 'full',
      padRatio: scaleMode === 'fit' ? 0.02 : 0.06,
    });
  }, [series, showNetWorth, showAssets, showLiabilities, showCloud, scaleMode]);

  const scale = yOverride ?? autoScale;

  // When the time window changes, autoScale moves — keep any Y zoom inside the new frame.
  useEffect(() => {
    setYOverride(prev => {
      if (!prev) return null;
      const next = clampYScale(prev.minVal, prev.maxVal, autoScale, scaleMode === 'full');
      if (Math.abs(next.range - autoScale.range) < 1e-6) return null;
      if (next.minVal === prev.minVal && next.maxVal === prev.maxVal) return prev;
      return next;
    });
  }, [autoScale, scaleMode]);

  const netWorthPoints = useMemo(
    () => valuesToPolylinePoints(series.netWorth, scale, viewBoxWidth, viewBoxHeight, padding),
    [series.netWorth, scale, viewBoxWidth, viewBoxHeight, padding]
  );
  const assetsPoints = useMemo(
    () => valuesToPolylinePoints(series.assets, scale, viewBoxWidth, viewBoxHeight, padding),
    [series.assets, scale, viewBoxWidth, viewBoxHeight, padding]
  );
  const liabilitiesPoints = useMemo(
    () => valuesToPolylinePoints(series.liabilities, scale, viewBoxWidth, viewBoxHeight, padding),
    [series.liabilities, scale, viewBoxWidth, viewBoxHeight, padding]
  );
  const smaPoints = useMemo(
    () => valuesToPolylinePoints(series.sma, scale, viewBoxWidth, viewBoxHeight, padding),
    [series.sma, scale, viewBoxWidth, viewBoxHeight, padding]
  );
  const emaPoints = useMemo(
    () => valuesToPolylinePoints(series.ema, scale, viewBoxWidth, viewBoxHeight, padding),
    [series.ema, scale, viewBoxWidth, viewBoxHeight, padding]
  );
  const areaPath = useMemo(
    () => valuesToAreaPath(series.netWorth, scale, viewBoxWidth, viewBoxHeight, padding),
    [series.netWorth, scale, viewBoxWidth, viewBoxHeight, padding]
  );
  const cloudPath = useMemo(
    () => valuesToCloudPath(series.ema, series.sma, scale, viewBoxWidth, viewBoxHeight, padding),
    [series.ema, series.sma, scale, viewBoxWidth, viewBoxHeight, padding]
  );

  const yTicks = useMemo(() => {
    const count = 5;
    return Array.from({ length: count }, (_, i) => {
      const t = i / (count - 1);
      const value = scale.maxVal - t * scale.range;
      return { value, y: valueToY(value, scale, viewBoxHeight, padding) };
    });
  }, [scale, viewBoxHeight, padding]);

  const xTicks = useMemo(() => {
    if (visibleSorted.length < 2) return [];
    const count = Math.min(5, visibleSorted.length);
    return Array.from({ length: count }, (_, i) => {
      const index = Math.round((i / (count - 1)) * (visibleSorted.length - 1));
      return {
        index,
        date: visibleSorted[index].date,
        x: indexToX(index, visibleSorted.length, viewBoxWidth, padding),
      };
    });
  }, [visibleSorted, viewBoxWidth, padding]);

  const rsiScale = useMemo(
    () => ({ minVal: 0, maxVal: 100, range: 100 }),
    []
  );
  const rsiPoints = useMemo(
    () => valuesToPolylinePoints(series.rsi, rsiScale, RSI_VIEWBOX.width, RSI_VIEWBOX.height, RSI_PADDING),
    [series.rsi, rsiScale]
  );
  const rsiTicks = [RSI_OVERBOUGHT, 50, RSI_OVERSOLD].map(value => ({
    value,
    y: valueToY(value, rsiScale, RSI_VIEWBOX.height, RSI_PADDING),
  }));

  const cloudReadyCount = series.sma.filter((v, i) => v != null && series.ema[i] != null).length;
  const maLabel = `${maPeriod}${intervalInfo.suffix}`;
  const cloudLabel = `${maLabel} Cloud`;

  const daysTracked = daily.length;
  const firstNetWorth = daily[0]?.netWorth ?? 0;
  const change = currentNetWorth - firstNetWorth;
  const isPositive = change >= 0;
  const changePct = firstNetWorth !== 0 ? (change / Math.abs(firstNetWorth)) * 100 : 0;
  const isZoomed =
    yOverride != null ||
    (xWindow != null && (xWindow.start > 0 || xWindow.end < maxIndex));

  const activeLocalIndex =
    hoverIndex != null
      ? hoverIndex
      : visibleSorted.length > 0
        ? visibleSorted.length - 1
        : null;
  const activeGlobalIndex =
    activeLocalIndex != null ? viewWindow.start + activeLocalIndex : null;
  const active = activeGlobalIndex != null ? sorted[activeGlobalIndex] : null;
  const activeLiability = active ? (active.totalLiabilities || 0) + (active.creditCards || 0) : 0;
  const activeSma = activeLocalIndex != null ? series.sma[activeLocalIndex] : null;
  const activeEma = activeLocalIndex != null ? series.ema[activeLocalIndex] : null;
  const activeRsi = activeLocalIndex != null ? series.rsi[activeLocalIndex] : null;

  const clientToLocalIndex = useCallback(
    (clientX: number, svg: SVGSVGElement) => {
      if (visibleSorted.length < 2) return 0;
      const rect = svg.getBoundingClientRect();
      const xRatio = (clientX - rect.left) / rect.width;
      const vbX = xRatio * viewBoxWidth;
      const chartWidth = viewBoxWidth - padding.left - padding.right;
      const clamped = Math.min(Math.max(vbX - padding.left, 0), chartWidth);
      return Math.round((clamped / chartWidth) * (visibleSorted.length - 1));
    },
    [visibleSorted.length, viewBoxWidth, padding]
  );

  const handlePointer = useCallback(
    (clientX: number) => {
      const svg = svgRef.current;
      if (!svg || visibleSorted.length < 2) return;
      setHoverIndex(clientToLocalIndex(clientX, svg));
    },
    [visibleSorted.length, clientToLocalIndex]
  );

  const resetZoom = useCallback(() => {
    setXWindow(null);
    setYOverride(null);
  }, []);

  const changeInterval = useCallback((next: ChartInterval) => {
    setChartInterval(next);
    setXWindow(null);
    setYOverride(null);
    setHoverIndex(null);
  }, []);

  const applyScaleMode = useCallback((mode: ScaleMode) => {
    setScaleMode(mode);
    setYOverride(null);
  }, []);

  // Center-based zoom only — cursor must not re-anchor between scroll gestures.
  const zoomXCentered = useCallback(
    (factor: number) => {
      const span = viewWindow.end - viewWindow.start;
      const nextSpan = Math.max(Math.min(MIN_X_SPAN, maxIndex), Math.round(span * factor));
      if (nextSpan >= maxIndex) {
        setXWindow(null);
        return;
      }
      const mid = (viewWindow.start + viewWindow.end) / 2;
      const nextStart = Math.round(mid - nextSpan / 2);
      setXWindow(clampWindow(nextStart, nextStart + nextSpan, maxIndex));
    },
    [viewWindow, maxIndex]
  );

  const zoomYCentered = useCallback(
    (factor: number) => {
      const current = yOverride ?? autoScale;
      const mid = (current.minVal + current.maxVal) / 2;
      const nextRange = current.range * factor;
      const next = clampYScale(
        mid - nextRange / 2,
        mid + nextRange / 2,
        autoScale,
        scaleMode === 'full'
      );
      // Fully zoomed out → drop override so Fit/Full chips stay accurate.
      if (Math.abs(next.range - autoScale.range) < 1e-6) {
        setYOverride(null);
      } else {
        setYOverride(next);
      }
    },
    [yOverride, autoScale, scaleMode]
  );

  const panXByPixels = useCallback(
    (dxPx: number, svgWidthPx: number) => {
      const chartWidth = viewBoxWidth - padding.left - padding.right;
      const span = viewWindow.end - viewWindow.start;
      if (span <= 0 || svgWidthPx <= 0) return;
      // Fractional accumulator so small drags still move time smoothly
      const indexDelta = -(dxPx / svgWidthPx) * viewBoxWidth * (span / chartWidth);
      dragRef.current.xAcc += indexDelta;
      const shift = Math.trunc(dragRef.current.xAcc);
      if (shift === 0) return;
      dragRef.current.xAcc -= shift;
      setXWindow(clampWindow(viewWindow.start + shift, viewWindow.end + shift, maxIndex));
    },
    [viewWindow, maxIndex, viewBoxWidth, padding]
  );

  const panYByPixels = useCallback(
    (dyPx: number, svgHeightPx: number) => {
      const chartHeight = viewBoxHeight - padding.top - padding.bottom;
      if (svgHeightPx <= 0 || dyPx === 0) return;
      const current = yOverride ?? autoScale;
      const valueDelta = (dyPx / svgHeightPx) * viewBoxHeight * (current.range / chartHeight);
      const next = clampYScale(
        current.minVal + valueDelta,
        current.maxVal + valueDelta,
        autoScale,
        scaleMode === 'full'
      );
      if (Math.abs(next.range - autoScale.range) < 1e-6 &&
          Math.abs(next.minVal - autoScale.minVal) < 1e-6) {
        setYOverride(null);
      } else {
        setYOverride(next);
      }
    },
    [yOverride, autoScale, scaleMode, viewBoxHeight, padding]
  );

  // Native non-passive wheel so zoom doesn't scroll the page
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const onWheel = (e: WheelEvent) => {
      if (visibleSorted.length < 2) return;
      e.preventDefault();
      const rect = svg.getBoundingClientRect();
      const vbX = ((e.clientX - rect.left) / rect.width) * viewBoxWidth;
      const factor = wheelZoomFactor(e.deltaY);
      // $ axis or Alt → depth; otherwise time. Always grow/shrink about center.
      if (vbX < padding.left || e.altKey) {
        zoomYCentered(factor);
      } else {
        zoomXCentered(factor);
      }
    };

    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [
    visibleSorted.length,
    viewBoxWidth,
    viewBoxHeight,
    padding,
    zoomXCentered,
    zoomYCentered,
  ]);

  const endDrag = useCallback((pointerId?: number) => {
    const drag = dragRef.current;
    if (pointerId != null && drag.pointerId != null && drag.pointerId !== pointerId) return;
    if (drag.pointerId != null) {
      try {
        svgRef.current?.releasePointerCapture(drag.pointerId);
      } catch {
        /* already released */
      }
    }
    dragRef.current = {
      kind: null,
      pointerId: null,
      lastClientX: 0,
      lastClientY: 0,
      xAcc: 0,
    };
    setIsDragging(false);
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const svg = svgRef.current;
      if (!svg || visibleSorted.length < 2) return;
      e.preventDefault();

      const pointers = pointersRef.current;
      // A new gesture always starts clean, even if an earlier pointerup was
      // never delivered (app switch, notification, and so on).
      if (e.isPrimary) {
        pointers.clear();
        pinchRef.current = null;
      }
      if (pointers.size >= 2) return; // a third finger changes nothing
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try {
        svg.setPointerCapture(e.pointerId);
      } catch {
        /* pointer already gone */
      }

      // Second finger down: stop panning and start a pinch on one axis.
      if (pointers.size === 2) {
        endDrag();
        const spread = pinchSpread(pointers);
        if (spread) {
          const axis = spread.dx >= spread.dy ? 'x' : 'y';
          pinchRef.current = {
            axis,
            startGap: Math.max(PINCH_MIN_GAP_PX, axis === 'x' ? spread.dx : spread.dy),
            startWindow: viewWindow,
            startY: yOverride ?? autoScale,
          };
        }
        return;
      }

      const rect = svg.getBoundingClientRect();
      const vbX = ((e.clientX - rect.left) / rect.width) * viewBoxWidth;
      const kind = vbX < padding.left ? 'pan-y' : 'pan-xy';
      dragRef.current = {
        kind,
        pointerId: e.pointerId,
        lastClientX: e.clientX,
        lastClientY: e.clientY,
        xAcc: 0,
      };
      setIsDragging(true);
      setHoverIndex(clientToLocalIndex(e.clientX, svg));
    },
    [viewBoxWidth, padding.left, visibleSorted.length, clientToLocalIndex, endDrag, viewWindow, yOverride, autoScale]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const pointers = pointersRef.current;
      if (pointers.has(e.pointerId)) {
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      }

      // Pinch: zoom the locked axis about its center, relative to pinch start.
      const pinch = pinchRef.current;
      if (pinch) {
        const spread = pinchSpread(pointers);
        if (!spread) return;
        const rawGap = pinch.axis === 'x' ? spread.dx : spread.dy;
        const gap = Math.max(PINCH_MIN_GAP_PX, rawGap);
        if (Math.abs(gap - pinch.startGap) < PINCH_DEAD_ZONE_PX) return;
        // Fingers apart -> zoom in -> smaller visible range.
        const factor = Math.pow(pinch.startGap / gap, PINCH_DAMPING);

        if (pinch.axis === 'y') {
          const start = pinch.startY;
          const mid = (start.minVal + start.maxVal) / 2;
          const nextRange = start.range * factor;
          const next = clampYScale(
            mid - nextRange / 2,
            mid + nextRange / 2,
            autoScale,
            scaleMode === 'full'
          );
          setYOverride(Math.abs(next.range - autoScale.range) < 1e-6 ? null : next);
          return;
        }

        const startSpan = pinch.startWindow.end - pinch.startWindow.start;
        const nextSpan = Math.round(startSpan * factor);
        if (nextSpan >= maxIndex) {
          setXWindow(null);
          return;
        }
        const span = Math.max(Math.min(MIN_X_SPAN, maxIndex), nextSpan);
        const mid = (pinch.startWindow.start + pinch.startWindow.end) / 2;
        const nextStart = Math.round(mid - span / 2);
        setXWindow(clampWindow(nextStart, nextStart + span, maxIndex));
        return;
      }

      const drag = dragRef.current;
      if (!drag.kind) {
        handlePointer(e.clientX);
        return;
      }
      // Only the finger that started the pan may move it.
      if (drag.pointerId !== e.pointerId) return;
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const dx = e.clientX - drag.lastClientX;
      const dy = e.clientY - drag.lastClientY;
      drag.lastClientX = e.clientX;
      drag.lastClientY = e.clientY;
      if (drag.kind === 'pan-xy') {
        panXByPixels(dx, rect.width);
        panYByPixels(dy, rect.height);
      } else {
        panYByPixels(dy, rect.height);
      }
      setHoverIndex(clientToLocalIndex(e.clientX, svg));
    },
    [handlePointer, panXByPixels, panYByPixels, clientToLocalIndex, maxIndex, autoScale, scaleMode]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const pointers = pointersRef.current;
      pointers.delete(e.pointerId);
      try {
        svgRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      if (pinchRef.current) {
        // Lifting one finger ends the pinch. The remaining finger does not
        // resume panning until it is lifted and put down again, so the chart
        // never jumps at the end of a pinch.
        if (pointers.size < 2) pinchRef.current = null;
        return;
      }
      endDrag(e.pointerId);
    },
    [endDrag]
  );

  if (daily.length < 2) {
    return (
      <div className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        <p className="text-sm font-medium">Not Enough History Yet</p>
        <p className="text-xs mt-1">Come back after a couple of days to see your trend chart.</p>
        <p className="text-xs mt-2">{daily.length} of 2 required data points collected</p>
      </div>
    );
  }

  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  const bright = darkMode ? 'text-white' : 'text-slate-900';
  const panel = darkMode ? 'bg-white/[0.04] border-white/10' : 'bg-slate-50 border-slate-200';
  const gridStroke = darkMode ? '#ffffff12' : '#00000010';
  const axisFill = darkMode ? '#94a3b8' : '#64748b';
  const crosshairStroke = darkMode ? '#e2e8f080' : '#64748b80';

  const legendBtn = (on: boolean) =>
    `flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-opacity ${
      on ? '' : 'opacity-35'
    } ${darkMode ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`;

  const chip = (on: boolean) =>
    `px-2 py-0.5 rounded text-[11px] font-semibold tracking-wide transition-colors ${
      on
        ? darkMode
          ? 'bg-violet-500/25 text-violet-200'
          : 'bg-violet-100 text-violet-700'
        : darkMode
          ? 'text-slate-500 hover:text-slate-300'
          : 'text-slate-400 hover:text-slate-600'
    }`;

  const lastLocalNw = series.netWorth[series.netWorth.length - 1];
  const lastNwY = valueToY(lastLocalNw, scale, viewBoxHeight, padding);
  const hoverX =
    activeLocalIndex != null
      ? indexToX(activeLocalIndex, visibleSorted.length, viewBoxWidth, padding)
      : null;
  const hoverNwY =
    activeLocalIndex != null && showNetWorth
      ? valueToY(series.netWorth[activeLocalIndex], scale, viewBoxHeight, padding)
      : null;
  const hoverRsiY =
    activeLocalIndex != null && activeRsi != null
      ? valueToY(activeRsi, rsiScale, RSI_VIEWBOX.height, RSI_PADDING)
      : null;

  return (
    <div className="space-y-3 py-1">
      {/* Hero */}
      <div className="text-center">
        <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${muted}`}>Net Worth Trend</div>
        <div className={`text-3xl font-bold ${bright}`}>{formatCurrency(currentNetWorth, privacyMode)}</div>
        <div className="mt-1 flex items-center justify-center gap-3 text-sm">
          <span className={`inline-flex items-center gap-1 font-semibold ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
            {isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {isPositive ? '+' : '−'}{formatCurrency(Math.abs(change), privacyMode)}
            <span className="font-medium opacity-80">({formatPercentage(changePct, 1, true)})</span>
          </span>
          <span className={muted}>·</span>
          <span className={muted}>{daysTracked} days tracked</span>
        </div>
      </div>

      {/* Cloud + scale toolbar */}
      <div className={`flex flex-wrap items-center justify-between gap-2 px-2 py-1.5 rounded-lg border ${panel}`}>
        <div className="flex items-center gap-1">
          <span className={`text-[10px] uppercase tracking-wider mr-1 ${muted}`}>View</span>
          {INTERVALS.map(i => (
            <button
              key={i.key}
              type="button"
              className={`${chip(interval === i.key)} disabled:opacity-40 disabled:cursor-not-allowed`}
              onClick={() => changeInterval(i.key)}
              disabled={intervalPointCounts[i.key] < 2}
              title={intervalPointCounts[i.key] < 2 ? `Needs at least 2 ${i.unit} of history` : `One point per ${i.key}`}
            >
              {i.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className={`text-[10px] uppercase tracking-wider mr-1 ${muted}`}>Cloud</span>
          {([20, 50, 200] as MaPeriod[]).map(period => (
            <button key={period} type="button" className={chip(maPeriod === period)} onClick={() => setMaPeriod(period)}>
              {period}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className={`text-[10px] uppercase tracking-wider mr-1 ${muted}`}>Scale</span>
          <button type="button" className={chip(scaleMode === 'full' && !yOverride)} onClick={() => applyScaleMode('full')}>
            Full
          </button>
          <button type="button" className={chip(scaleMode === 'fit' && !yOverride)} onClick={() => applyScaleMode('fit')}>
            Fit
          </button>
          {isZoomed && (
            <button
              type="button"
              className={`inline-flex items-center gap-1 ${chip(false)}`}
              onClick={resetZoom}
              title="Reset zoom"
            >
              <RotateCcw size={11} />
              Reset
            </button>
          )}
        </div>
        {cloudReadyCount === 0 && (
          <span className={`w-full text-[10px] ${muted}`}>
            Need {maPeriod} {intervalInfo.unit} for {cloudLabel} ({sorted.length} so far)
          </span>
        )}
        <span className={`w-full text-[10px] ${muted}`}>
          Click-drag to pan · scroll to zoom time · $ axis or Alt-scroll to zoom depth (center grow/shrink) · Fit = widget depth · Full keeps $0
        </span>
      </div>

      {/* Main chart */}
      <div className="relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
          className={`w-full max-h-[42vh] touch-none select-none ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDoubleClick={resetZoom}
        >
          <defs>
            <linearGradient id={areaFillId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={HISTORY_LINE_COLORS.netWorth} stopOpacity={darkMode ? 0.3 : 0.2} />
              <stop offset="100%" stopColor={HISTORY_LINE_COLORS.netWorth} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Grid + Y labels */}
          {yTicks.map((tick, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                y1={tick.y}
                x2={viewBoxWidth - padding.right}
                y2={tick.y}
                stroke={gridStroke}
                strokeWidth="1"
              />
              <text
                x={padding.left - 6}
                y={tick.y + 3}
                fill={axisFill}
                fontSize="9"
                textAnchor="end"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              >
                {formatCompactCurrency(tick.value, privacyMode)}
              </text>
            </g>
          ))}

          {/* X labels */}
          {xTicks.map((tick, i) => (
            <text
              key={i}
              x={tick.x}
              y={viewBoxHeight - 8}
              fill={axisFill}
              fontSize="8"
              textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              {tick.date.slice(5)}
            </text>
          ))}

          {showNetWorth && areaPath && <path d={areaPath} fill={`url(#${areaFillId})`} />}

          {showCloud && cloudPath && (
            <path
              d={cloudPath}
              fill={HISTORY_LINE_COLORS.cloud}
              fillOpacity={darkMode ? 0.22 : 0.16}
              stroke="none"
            />
          )}

          {showLiabilities && liabilitiesPoints && (
            <polyline
              points={liabilitiesPoints}
              fill="none"
              stroke={HISTORY_LINE_COLORS.liabilities}
              strokeWidth="1.5"
              strokeDasharray="5 3"
              opacity="0.85"
              strokeLinejoin="round"
            />
          )}
          {showAssets && assetsPoints && (
            <polyline
              points={assetsPoints}
              fill="none"
              stroke={HISTORY_LINE_COLORS.assets}
              strokeWidth="1.5"
              strokeDasharray="5 3"
              opacity="0.85"
              strokeLinejoin="round"
            />
          )}
          {showNetWorth && netWorthPoints && (
            <polyline
              points={netWorthPoints}
              fill="none"
              stroke={HISTORY_LINE_COLORS.netWorth}
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {/* Cloud lines on top — EMA blue, SMA rose */}
          {showCloud && smaPoints && (
            <polyline
              points={smaPoints}
              fill="none"
              stroke={HISTORY_LINE_COLORS.sma}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {showCloud && emaPoints && (
            <polyline
              points={emaPoints}
              fill="none"
              stroke={HISTORY_LINE_COLORS.ema}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Last price tag */}
          {showNetWorth && (
            <g>
              <line
                x1={padding.left}
                y1={lastNwY}
                x2={viewBoxWidth - padding.right}
                y2={lastNwY}
                stroke={HISTORY_LINE_COLORS.netWorth}
                strokeWidth="0.75"
                strokeDasharray="3 3"
                opacity="0.35"
              />
              <rect
                x={viewBoxWidth - padding.right + 2}
                y={lastNwY - 8}
                width="50"
                height="16"
                rx="2"
                fill={HISTORY_LINE_COLORS.netWorth}
              />
              <text
                x={viewBoxWidth - padding.right + 27}
                y={lastNwY + 3.5}
                fill="#fff"
                fontSize="8"
                textAnchor="middle"
                fontWeight="700"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              >
                {formatCompactCurrency(lastLocalNw, privacyMode)}
              </text>
            </g>
          )}

          {/* Crosshair */}
          {hoverIndex != null && hoverX != null && (
            <g>
              <line
                x1={hoverX}
                y1={padding.top}
                x2={hoverX}
                y2={viewBoxHeight - padding.bottom}
                stroke={crosshairStroke}
                strokeWidth="1"
                strokeDasharray="3 2"
              />
              {hoverNwY != null && (
                <>
                  <line
                    x1={padding.left}
                    y1={hoverNwY}
                    x2={viewBoxWidth - padding.right}
                    y2={hoverNwY}
                    stroke={crosshairStroke}
                    strokeWidth="1"
                    strokeDasharray="3 2"
                  />
                  <circle
                    cx={hoverX}
                    cy={hoverNwY}
                    r="3.5"
                    fill={HISTORY_LINE_COLORS.netWorth}
                    stroke={darkMode ? '#0f172a' : '#fff'}
                    strokeWidth="1.5"
                  />
                </>
              )}
            </g>
          )}
        </svg>

        {/* RSI pane */}
        {showRsi && (
          <svg
            ref={rsiSvgRef}
            viewBox={`0 0 ${RSI_VIEWBOX.width} ${RSI_VIEWBOX.height}`}
            className="w-full max-h-[18vh] touch-none select-none mt-1"
            onMouseMove={e => handlePointer(e.clientX)}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <text
              x={RSI_PADDING.left}
              y={10}
              fill={axisFill}
              fontSize="8"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              RSI {RSI_PERIOD}
            </text>
            {rsiTicks.map(tick => (
              <g key={tick.value}>
                <line
                  x1={RSI_PADDING.left}
                  y1={tick.y}
                  x2={RSI_VIEWBOX.width - RSI_PADDING.right}
                  y2={tick.y}
                  stroke={
                    tick.value === RSI_OVERBOUGHT
                      ? '#ef444466'
                      : tick.value === RSI_OVERSOLD
                        ? '#22c55e66'
                        : gridStroke
                  }
                  strokeWidth="1"
                  strokeDasharray={tick.value === 50 ? '2 3' : '4 3'}
                />
                <text
                  x={RSI_PADDING.left - 6}
                  y={tick.y + 3}
                  fill={axisFill}
                  fontSize="8"
                  textAnchor="end"
                  fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                >
                  {tick.value}
                </text>
              </g>
            ))}
            {rsiPoints && (
              <polyline
                points={rsiPoints}
                fill="none"
                stroke={HISTORY_LINE_COLORS.rsi}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {hoverIndex != null && hoverX != null && (
              <g>
                <line
                  x1={hoverX}
                  y1={RSI_PADDING.top}
                  x2={hoverX}
                  y2={RSI_VIEWBOX.height - RSI_PADDING.bottom}
                  stroke={crosshairStroke}
                  strokeWidth="1"
                  strokeDasharray="3 2"
                />
                {hoverRsiY != null && (
                  <circle
                    cx={hoverX}
                    cy={hoverRsiY}
                    r="3"
                    fill={HISTORY_LINE_COLORS.rsi}
                    stroke={darkMode ? '#0f172a' : '#fff'}
                    strokeWidth="1.5"
                  />
                )}
              </g>
            )}
          </svg>
        )}

        {/* Hover readout */}
        {active && (
          <div
            className={`mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 py-2 rounded-lg border text-xs ${panel}`}
          >
            <div>
              <div className={muted}>Date</div>
              <div className={`font-semibold tabular-nums ${bright}`}>{active.date}</div>
            </div>
            <div>
              <div className={muted}>Net Worth</div>
              <div className="font-semibold tabular-nums" style={{ color: HISTORY_LINE_COLORS.netWorth }}>
                {formatCurrency(active.netWorth, privacyMode)}
              </div>
            </div>
            <div>
              <div className={muted}>Assets</div>
              <div className="font-semibold tabular-nums" style={{ color: HISTORY_LINE_COLORS.assets }}>
                {formatCurrency(active.totalAssets, privacyMode)}
              </div>
            </div>
            <div>
              <div className={muted}>Liabilities</div>
              <div className="font-semibold tabular-nums" style={{ color: HISTORY_LINE_COLORS.liabilities }}>
                {formatCurrency(activeLiability, privacyMode)}
              </div>
            </div>
            {showCloud && (
              <>
                <div>
                  <div className={muted}>{maLabel} SMA</div>
                  <div className="font-semibold tabular-nums" style={{ color: HISTORY_LINE_COLORS.sma }}>
                    {activeSma != null ? formatCurrency(activeSma, privacyMode) : '—'}
                  </div>
                </div>
                <div>
                  <div className={muted}>{maLabel} EMA</div>
                  <div className="font-semibold tabular-nums" style={{ color: HISTORY_LINE_COLORS.ema }}>
                    {activeEma != null ? formatCurrency(activeEma, privacyMode) : '—'}
                  </div>
                </div>
              </>
            )}
            {showRsi && (
              <div>
                <div className={muted}>RSI {RSI_PERIOD}</div>
                <div className="font-semibold tabular-nums" style={{ color: HISTORY_LINE_COLORS.rsi }}>
                  {activeRsi != null ? activeRsi.toFixed(1) : '—'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clickable legend */}
      <div className="flex flex-wrap justify-center gap-1">
        <button type="button" className={legendBtn(showNetWorth)} onClick={() => setShowNetWorth(v => !v)}>
          <div className="w-3 h-0.5 rounded" style={{ backgroundColor: HISTORY_LINE_COLORS.netWorth }} />
          <span className={muted}>Net Worth</span>
        </button>
        <button type="button" className={legendBtn(showAssets)} onClick={() => setShowAssets(v => !v)}>
          <div className="w-3 h-0.5 rounded" style={{ backgroundColor: HISTORY_LINE_COLORS.assets }} />
          <span className={muted}>Assets</span>
        </button>
        <button type="button" className={legendBtn(showLiabilities)} onClick={() => setShowLiabilities(v => !v)}>
          <div className="w-3 h-0.5 rounded" style={{ backgroundColor: HISTORY_LINE_COLORS.liabilities }} />
          <span className={muted}>Liabilities</span>
        </button>
        <button type="button" className={legendBtn(showCloud)} onClick={() => setShowCloud(v => !v)}>
          <div className="flex items-center gap-0.5">
            <div className="w-2 h-0.5 rounded" style={{ backgroundColor: HISTORY_LINE_COLORS.ema }} />
            <div className="w-2 h-0.5 rounded" style={{ backgroundColor: HISTORY_LINE_COLORS.sma }} />
          </div>
          <span className={muted}>{cloudLabel}</span>
        </button>
        <button type="button" className={legendBtn(showRsi)} onClick={() => setShowRsi(v => !v)}>
          <div className="w-3 h-0.5 rounded" style={{ backgroundColor: HISTORY_LINE_COLORS.rsi }} />
          <span className={muted}>RSI</span>
        </button>
      </div>
    </div>
  );
};

const CATEGORY_LABELS: Record<string, string> = {
  banking: 'Cash',
  investment: 'Stocks',
  retirement: '401k',
  crypto: 'Crypto',
  privateEquity: 'PE',
  asset: 'Assets',
  hsa: 'HSA',
};

const AllocationTab: React.FC<AllocationTabProps> = ({
  darkMode,
  privacyMode,
  chartData,
}) => {
  // Selected category for drill-down view (null = overview)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Aggregate chartData by category type
  const categoryData = useMemo(() => {
    const typeMap = new Map<string, number>();
    for (const d of chartData) {
      typeMap.set(d.type, (typeMap.get(d.type) || 0) + d.value);
    }
    const items = Array.from(typeMap.entries())
      .map(([type, value]) => ({ name: CATEGORY_LABELS[type] || type, value, type }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);

    const total = items.reduce((sum, d) => sum + d.value, 0);
    let cumulative = 0;
    return items.map((item, idx) => {
      const percentage = total > 0 ? (item.value / total) * 100 : 0;
      const startPercentage = cumulative;
      cumulative += percentage;
      return { ...item, percentage, startPercentage, color: CHART_COLORS[idx % CHART_COLORS.length] };
    });
  }, [chartData]);

  // Drill-down: individual holdings within the selected category
  const drillDownData = useMemo(() => {
    if (!selectedCategory) return [];
    // Merge duplicate tickers within the category
    const merged = new Map<string, number>();
    for (const d of chartData) {
      if (d.type === selectedCategory) {
        merged.set(d.name, (merged.get(d.name) || 0) + d.value);
      }
    }
    const items = Array.from(merged.entries())
      .map(([name, value]) => ({ name, value }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);

    const total = items.reduce((sum, d) => sum + d.value, 0);
    let cumulative = 0;
    return items.map((item, idx) => {
      const percentage = total > 0 ? (item.value / total) * 100 : 0;
      const startPercentage = cumulative;
      cumulative += percentage;
      return { ...item, percentage, startPercentage, color: CHART_COLORS[idx % CHART_COLORS.length] };
    });
  }, [chartData, selectedCategory]);

  const presentTypes = useMemo(() => categoryData.map(d => d.type), [categoryData]);
  const [enabledTypes, setEnabledTypes] = useState<Set<string>>(() => new Set(presentTypes));

  // Sync enabled types when chartData categories change
  useEffect(() => {
    setEnabledTypes(prev => {
      const updated = new Set(prev);
      for (const t of presentTypes) {
        if (!updated.has(t)) updated.add(t);
      }
      return updated;
    });
  }, [presentTypes]);

  const [showDollars, setShowDollars] = useState(true);

  // Filter and recalculate percentages (keep stable colors from categoryData)
  const filteredData = useMemo(() => {
    const items = categoryData.filter(d => enabledTypes.has(d.type));
    const total = items.reduce((sum, item) => sum + item.value, 0);
    let cumulative = 0;
    return items.map((item) => {
      const percentage = total > 0 ? (item.value / total) * 100 : 0;
      const startPercentage = cumulative;
      cumulative += percentage;
      return { ...item, percentage, startPercentage };
    });
  }, [categoryData, enabledTypes]);

  // Colors keyed by type for the pills — matches chart slice colors
  const categoryColors = useMemo(() => {
    const colors: Record<string, string> = {};
    categoryData.forEach((d, i) => {
      colors[d.type] = CHART_COLORS[i % CHART_COLORS.length];
    });
    return colors;
  }, [categoryData]);

  if (chartData.length === 0) {
    return (
      <div className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>No assets with positive value to show</div>
    );
  }

  // Determine which data set + labels to render
  const isDetailView = selectedCategory !== null;
  const displayData = isDetailView ? drillDownData : filteredData;
  const displayTotal = displayData.reduce((sum, item) => sum + item.value, 0);
  const selectedCategoryLabel = selectedCategory ? (CATEGORY_LABELS[selectedCategory] || selectedCategory) : '';

  return (
    <div className="py-2 space-y-4">
      {/* Category toggle pills + display mode toggle (overview only) */}
      {!isDetailView ? (
        <div className="flex flex-wrap gap-1.5 justify-center items-center">
          {presentTypes.map(type => {
            const enabled = enabledTypes.has(type);
            return (
              <button
                key={type}
                onClick={() => setSelectedCategory(type)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
                  enabled
                    ? darkMode
                      ? 'bg-white/10 text-slate-200 hover:bg-white/20'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    : darkMode
                      ? 'bg-white/5 text-slate-600'
                      : 'bg-slate-50 text-slate-400'
                }`}
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0 transition-opacity"
                  style={{ backgroundColor: categoryColors[type], opacity: enabled ? 1 : 0.3 }}
                />
                {CATEGORY_LABELS[type] || type}
              </button>
            );
          })}
          <div className={`w-px h-4 mx-0.5 ${darkMode ? 'bg-white/10' : 'bg-slate-200'}`} />
          <button
            onClick={() => setShowDollars(prev => !prev)}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
              darkMode
                ? 'bg-white/10 text-slate-200 hover:bg-white/15'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {showDollars ? '$' : '%'}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all ${
              darkMode
                ? 'bg-white/10 text-slate-200 hover:bg-white/15'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <span className="text-sm">&larr;</span> All Categories
          </button>
          <span className={`text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            {selectedCategoryLabel} Breakdown
          </span>
          <div className={`w-px h-4 mx-0.5 ${darkMode ? 'bg-white/10' : 'bg-slate-200'}`} />
          <button
            onClick={() => setShowDollars(prev => !prev)}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
              darkMode
                ? 'bg-white/10 text-slate-200 hover:bg-white/15'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {showDollars ? '$' : '%'}
          </button>
        </div>
      )}

      {displayData.length === 0 ? (
        <div className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          <p className="text-sm">{isDetailView ? 'No holdings in this category' : 'All categories hidden'}</p>
          {!isDetailView && <p className="text-xs mt-1">Toggle a category above to view allocations</p>}
        </div>
      ) : (
        <div className="flex justify-center">
          <div className="w-full max-w-2xl">
            <svg viewBox="-55 -40 210 180" className="w-full h-auto overflow-visible">
              <g transform="rotate(-90 50 50)">
                {displayData.map((item, idx) => {
                  const circumference = 2 * Math.PI * 40;
                  const dashArray = (item.percentage / 100) * circumference;
                  const dashOffset = -(item.startPercentage / 100) * circumference;
                  return (
                    <circle
                      key={idx} cx="50" cy="50" r="40" fill="none" stroke={item.color} strokeWidth="18"
                      strokeDasharray={`${dashArray} ${circumference}`} strokeDashoffset={dashOffset}
                      className={!isDetailView && 'type' in item ? 'cursor-pointer' : ''}
                      onClick={!isDetailView && 'type' in item ? () => setSelectedCategory((item as typeof categoryData[0]).type) : undefined}
                    />
                  );
                })}
                <circle cx="50" cy="50" r="31" fill={darkMode ? '#1f2937' : '#ffffff'} />
              </g>
              {/* Leader lines + labels with collision avoidance */}
              {(() => {
                const MIN_LABEL_GAP = 8;
                // Build raw label positions
                const rawLabels = displayData.map((item, idx) => {
                  if (item.percentage < 2) return null;
                  const midAngle = ((item.startPercentage + item.percentage / 2) / 100) * 360 - 90;
                  const rad = (midAngle * Math.PI) / 180;
                  const outerR = 62;
                  const x2 = 50 + outerR * Math.cos(rad);
                  const y2 = 50 + outerR * Math.sin(rad);
                  const isRight = x2 >= 50;
                  return { item, idx, midAngle, rad, x2, y2, isRight, adjustedY: y2 };
                }).filter(Boolean) as Array<{ item: typeof displayData[0]; idx: number; midAngle: number; rad: number; x2: number; y2: number; isRight: boolean; adjustedY: number }>;

                // Separate left and right labels, sort by Y, push apart if overlapping
                for (const side of [true, false]) {
                  const sideLabels = rawLabels.filter(l => l.isRight === side).sort((a, b) => a.y2 - b.y2);
                  for (let i = 1; i < sideLabels.length; i++) {
                    const prev = sideLabels[i - 1];
                    const curr = sideLabels[i];
                    if (curr.adjustedY - prev.adjustedY < MIN_LABEL_GAP) {
                      curr.adjustedY = prev.adjustedY + MIN_LABEL_GAP;
                    }
                  }
                }

                return rawLabels.map(({ item, idx, rad, x2, adjustedY, isRight }) => {
                  const innerR = 52;
                  const x1 = 50 + innerR * Math.cos(rad);
                  const y1 = 50 + innerR * Math.sin(rad);
                  const x3 = isRight ? x2 + 6 : x2 - 6;
                  const labelX = x3 + (isRight ? 1 : -1);
                  const nameLine = item.name;
                  const valueLine = showDollars
                    ? (privacyMode ? '•••' : formatCurrency(item.value, false))
                    : item.percentage.toFixed(1) + '%';
                  return (
                    <g
                      key={`label-${idx}`}
                      className={!isDetailView && 'type' in item ? 'cursor-pointer' : ''}
                      onClick={!isDetailView && 'type' in item ? () => setSelectedCategory((item as typeof categoryData[0]).type) : undefined}
                    >
                      <line x1={x1} y1={y1} x2={x2} y2={adjustedY} stroke={item.color} strokeWidth="0.4" />
                      <line x1={x2} y1={adjustedY} x2={x3} y2={adjustedY} stroke={item.color} strokeWidth="0.4" />
                      <text
                        x={labelX}
                        y={adjustedY}
                        textAnchor={isRight ? 'start' : 'end'}
                        fill={darkMode ? '#d1d5db' : '#4b5563'}
                        fontSize="3.5"
                        fontWeight="600"
                      >
                        <tspan x={labelX} dy="-1.5">{nameLine}</tspan>
                        <tspan x={labelX} dy="4.5" fontWeight="500" fontSize="3">{valueLine}</tspan>
                      </text>
                    </g>
                  );
                });
              })()}
              {/* Center label */}
              {showDollars ? (
                <>
                  <text x="50" y="46" textAnchor="middle" dominantBaseline="auto" fill={darkMode ? '#9ca3af' : '#9ca3af'} fontSize="5" fontWeight="500">{isDetailView ? selectedCategoryLabel : 'Total'}</text>
                  <text x="50" y="55" textAnchor="middle" dominantBaseline="auto" fill={darkMode ? '#ffffff' : '#111827'} fontSize={formatCurrency(displayTotal, privacyMode).length > 10 ? '5' : formatCurrency(displayTotal, privacyMode).length > 8 ? '6' : '7'} fontWeight="700">{formatCurrency(displayTotal, privacyMode)}</text>
                </>
              ) : (
                <text x="50" y="52" textAnchor="middle" dominantBaseline="auto" fill={darkMode ? '#9ca3af' : '#6b7280'} fontSize="5" fontWeight="500">{isDetailView ? selectedCategoryLabel : 'Allocation'}</text>
              )}
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── NEW Chart Sub-components ────────────────────────────────────────────────

// Chart 1: Budget Donut
interface BudgetDonutTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  budgetUtilization: Array<{ category: string; budgeted: number; spent: number; percentage: number; remaining: number; color: string }>;
  totalSpent: number;
  totalBudgeted: number;
}

const BudgetDonutTab: React.FC<BudgetDonutTabProps> = ({ darkMode, privacyMode, budgetUtilization, totalSpent, totalBudgeted }) => {
  if (budgetUtilization.length === 0) {
    return (
      <div className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        <p className="text-sm">No budget categories</p>
        <p className="text-xs mt-1">Add budget categories to see spending breakdown</p>
      </div>
    );
  }

  const totalForChart = budgetUtilization.reduce((sum, cat) => sum + cat.spent, 0) || 1;
  let cumulative = 0;
  const segments = budgetUtilization
    .filter(cat => cat.spent > 0)
    .map((cat, idx) => {
      const pct = (cat.spent / totalForChart) * 100;
      const start = cumulative;
      cumulative += pct;
      const isOverBudget = cat.spent > cat.budgeted;
      return { ...cat, pct, start, chartColor: isOverBudget ? '#ef4444' : CHART_COLORS[idx % CHART_COLORS.length] };
    });

  const overallPct = totalBudgeted > 0 ? Math.round((totalSpent / totalBudgeted) * 100) : 0;

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center justify-center">
        <div className="relative w-44 h-44 sm:w-52 sm:h-52">
          <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
            <circle cx="50" cy="50" r="40" fill="none" stroke={darkMode ? '#ffffff10' : '#00000008'} strokeWidth="16" />
            {segments.map((seg, idx) => {
              const circumference = 2 * Math.PI * 40;
              const dashArray = (seg.pct / 100) * circumference;
              const dashOffset = -(seg.start / 100) * circumference;
              return (
                <circle key={idx} cx="50" cy="50" r="40" fill="none" stroke={seg.chartColor} strokeWidth="16"
                  strokeDasharray={`${dashArray} ${circumference}`} strokeDashoffset={dashOffset} />
              );
            })}
            <circle cx="50" cy="50" r="31" fill={darkMode ? '#1f2937' : '#ffffff'} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{privacyMode ? '•••' : `${overallPct}%`}</div>
            <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>of budget used</div>
          </div>
        </div>
      </div>

      <div className={`border-t ${darkMode ? 'border-white/10' : 'border-slate-100'} pt-3 space-y-2.5`}>
        {budgetUtilization.map((cat, idx) => {
          const pct = cat.budgeted > 0 ? Math.min((cat.spent / cat.budgeted) * 100, 100) : 0;
          const isOver = cat.spent > cat.budgeted;
          return (
            <div key={idx}>
              <div className="flex justify-between items-center mb-1">
                <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{cat.category}</span>
                <span className={`text-xs font-semibold ${isOver ? 'text-red-500' : darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {formatCurrency(cat.spent, privacyMode)} / {formatCurrency(cat.budgeted, privacyMode)}
                </span>
              </div>
              <div className={`h-1.5 rounded-full ${darkMode ? 'bg-white/10' : 'bg-slate-100'}`}>
                <div className={`h-full rounded-full ${isOver ? 'bg-red-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Chart 2: Credit Utilization Gauges
interface CreditGaugesTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  creditCardUtilization: Array<{ name: string; balance: number; limit: number; utilization: number }>;
}

const CreditGaugesTab: React.FC<CreditGaugesTabProps> = ({ darkMode, privacyMode, creditCardUtilization }) => {
  if (creditCardUtilization.length === 0) {
    return (
      <div className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        <p className="text-sm">No credit cards</p>
        <p className="text-xs mt-1">Add credit cards to see utilization</p>
      </div>
    );
  }

  const getGaugeColor = (util: number) => {
    if (util > 70) return '#ef4444';
    if (util > 30) return '#f59e0b';
    return '#22c55e';
  };

  const totalBalance = creditCardUtilization.reduce((s, c) => s + c.balance, 0);
  const totalLimit = creditCardUtilization.reduce((s, c) => s + c.limit, 0);
  const totalUtil = totalLimit > 0 ? (totalBalance / totalLimit) * 100 : 0;

  return (
    <div className="space-y-4 py-2">
      <div className="text-center">
        <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Overall Utilization</div>
        <div className={`text-3xl font-bold ${getGaugeColor(totalUtil) === '#22c55e' ? 'text-green-500' : getGaugeColor(totalUtil) === '#f59e0b' ? 'text-yellow-500' : 'text-red-500'}`}>
          {privacyMode ? '•••' : `${Math.round(totalUtil)}%`}
        </div>
        <div className={`text-xs mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          {formatCurrency(totalBalance, privacyMode)} / {formatCurrency(totalLimit, privacyMode)}
        </div>
      </div>

      <div className={`border-t ${darkMode ? 'border-white/10' : 'border-slate-100'} pt-3 space-y-3`}>
        {creditCardUtilization.map((card, idx) => {
          const color = getGaugeColor(card.utilization);
          const barPct = Math.min(card.utilization, 100);
          return (
            <div key={idx}>
              <div className="flex justify-between items-center mb-1">
                <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{card.name}</span>
                <span className="text-sm font-bold" style={{ color }}>{privacyMode ? '•••' : `${Math.round(card.utilization)}%`}</span>
              </div>
              <div className={`h-1.5 rounded-full ${darkMode ? 'bg-white/10' : 'bg-slate-100'}`}>
                <div className="h-full rounded-full transition-all" style={{ width: `${barPct}%`, backgroundColor: color }} />
              </div>
              <div className={`text-xs mt-0.5 ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                {formatCurrency(card.balance, privacyMode)} / {formatCurrency(card.limit, privacyMode)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Chart 3: Goals Progress Bars
interface GoalsProgressTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  goalProgress: Array<{ name: string; targetAmount: number; currentAmount: number; targetDate: string; percentage: number; remaining: number; onTrack: boolean; color: string }>;
}

const GoalsProgressTab: React.FC<GoalsProgressTabProps> = ({ darkMode, privacyMode, goalProgress }) => {
  if (goalProgress.length === 0) {
    return (
      <div className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        <p className="text-sm">No goals set</p>
        <p className="text-xs mt-1">Add goals to track your progress</p>
      </div>
    );
  }

  const completed = goalProgress.filter(g => g.percentage >= 100).length;

  return (
    <div className="space-y-4 py-2">
      <div className="text-center">
        <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Goals</div>
        <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
          {completed} <span className={`text-lg font-normal ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>/ {goalProgress.length} complete</span>
        </div>
      </div>

      <div className={`border-t ${darkMode ? 'border-white/10' : 'border-slate-100'} pt-3 space-y-3`}>
        {goalProgress.map((goal, idx) => {
          const isComplete = goal.percentage >= 100;
          const barPct = Math.min(goal.percentage, 100);
          const barColor = isComplete ? 'bg-green-500' : goal.onTrack ? 'bg-green-500' : 'bg-yellow-500';
          return (
            <div key={idx}>
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-1.5">
                  {isComplete && <Check size={12} className="text-green-500" />}
                  <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{goal.name}</span>
                </div>
                <span className={`text-sm font-bold ${isComplete ? 'text-green-500' : goal.onTrack ? 'text-green-500' : 'text-yellow-500'}`}>
                  {privacyMode ? '•••' : `${Math.round(goal.percentage)}%`}
                </span>
              </div>
              <div className={`h-1.5 rounded-full ${darkMode ? 'bg-white/10' : 'bg-slate-100'}`}>
                <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${barPct}%` }} />
              </div>
              <div className="flex justify-between items-center mt-0.5">
                <span className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                  {formatCurrency(goal.currentAmount, privacyMode)} / {formatCurrency(goal.targetAmount, privacyMode)}
                </span>
                {goal.targetDate && <span className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>{goal.targetDate}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Chart 4: Net Worth Stacked Area
interface StackedAreaTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  snapshots: NetWorthSnapshot[];
}

const STACKED_COLORS = { banking: '#3b82f6', investments: '#8b5cf6', retirement: '#10b981', crypto: '#f59e0b' };

const StackedAreaTab: React.FC<StackedAreaTabProps> = ({ darkMode, privacyMode, snapshots }) => {
  if (snapshots.length < 2) {
    return (
      <div className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        <p className="text-sm font-medium">Not Enough History Yet</p>
        <p className="text-xs mt-1">Need at least 2 snapshots for composition chart.</p>
        <p className="text-xs mt-2">{snapshots.length} of 2 required data points collected</p>
      </div>
    );
  }

  const viewBoxWidth = 400;
  const viewBoxHeight = 200;
  const padding = 30;
  const chartWidth = viewBoxWidth - padding * 2;
  const chartHeight = viewBoxHeight - padding * 2;

  const layers: { key: string; color: string; label: string }[] = [
    { key: 'banking', color: STACKED_COLORS.banking, label: 'Banking' },
    { key: 'investments', color: STACKED_COLORS.investments, label: 'Investments' },
    { key: 'retirement401k', color: STACKED_COLORS.retirement, label: '401k' },
    { key: 'crypto', color: STACKED_COLORS.crypto, label: 'Crypto' },
  ];

  const stackedValues = snapshots.map(s => {
    const vals = [Math.max(0, s.banking), Math.max(0, s.investments), Math.max(0, s.retirement401k), Math.max(0, s.crypto || 0)];
    const cumulative: number[] = [];
    vals.reduce((acc, v) => { cumulative.push(acc + v); return acc + v; }, 0);
    return { raw: vals, cumulative };
  });

  const maxVal = Math.max(...stackedValues.map(sv => sv.cumulative[sv.cumulative.length - 1] || 0), 1);
  const getX = (i: number) => padding + (i / (snapshots.length - 1)) * chartWidth;
  const getY = (val: number) => padding + chartHeight - (val / maxVal) * chartHeight;
  const firstDate = snapshots[0].date;
  const lastDate = snapshots[snapshots.length - 1].date;

  return (
    <div className="space-y-3 py-2">
      <svg viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} className="w-full max-h-[35vh]">
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line key={i} x1={padding} y1={padding + chartHeight * pct} x2={viewBoxWidth - padding} y2={padding + chartHeight * pct}
            stroke={darkMode ? '#ffffff15' : '#00000010'} strokeWidth="0.5" />
        ))}
        {[...layers].reverse().map((layer, layerRevIdx) => {
          const layerIdx = layers.length - 1 - layerRevIdx;
          const topPoints = snapshots.map((_, i) => `${getX(i).toFixed(1)},${getY(stackedValues[i].cumulative[layerIdx]).toFixed(1)}`);
          const bottomPoints = snapshots.map((_, i) => {
            const bottomVal = layerIdx > 0 ? stackedValues[i].cumulative[layerIdx - 1] : 0;
            return `${getX(i).toFixed(1)},${getY(bottomVal).toFixed(1)}`;
          });
          const polygonPoints = [...topPoints, ...bottomPoints.reverse()].join(' ');
          return <polygon key={layer.key} points={polygonPoints} fill={layer.color} fillOpacity="0.4" stroke={layer.color} strokeWidth="1.5" />;
        })}
        <text x={padding} y={viewBoxHeight - 5} fill={darkMode ? '#9ca3af' : '#6b7280'} fontSize="6" textAnchor="start">{firstDate}</text>
        <text x={viewBoxWidth - padding} y={viewBoxHeight - 5} fill={darkMode ? '#9ca3af' : '#6b7280'} fontSize="6" textAnchor="end">{lastDate}</text>
      </svg>

      <div className="flex justify-center gap-5">
        {layers.map(layer => (
          <div key={layer.key} className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: layer.color }} />
            <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{layer.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Chart 5: Gain/Loss Waterfall
interface TreemapTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  investments: Investment[];
  cryptoHoldings: CryptoHolding[];
}

const TreemapTab: React.FC<TreemapTabProps> = ({ darkMode, privacyMode, investments, cryptoHoldings }) => {
  // Aggregate by symbol across all accounts
  const symbolMap = new Map<string, { value: number; totalCost: number }>();

  investments.filter(inv => inv.totalValue > 0).forEach(inv => {
    const existing = symbolMap.get(inv.symbol) || { value: 0, totalCost: 0 };
    existing.value += inv.totalValue;
    existing.totalCost += inv.shares * inv.costBasis;
    symbolMap.set(inv.symbol, existing);
  });

  cryptoHoldings.filter(h => h.totalValue > 0).forEach(h => {
    const existing = symbolMap.get(h.symbol) || { value: 0, totalCost: 0 };
    existing.value += h.totalValue;
    existing.totalCost += h.amount * h.costBasis;
    symbolMap.set(h.symbol, existing);
  });

  const holdings = Array.from(symbolMap.entries()).map(([symbol, data]) => {
    const gain = data.value - data.totalCost;
    const gainPct = data.totalCost > 0 ? (gain / data.totalCost) * 100 : 0;
    return { label: symbol, value: data.value, gain, gainPct };
  }).sort((a, b) => b.gainPct - a.gainPct);

  if (holdings.length === 0) {
    return (
      <div className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        <p className="text-sm">No holdings</p>
        <p className="text-xs mt-1">Add investments to see gain/loss</p>
      </div>
    );
  }

  const maxAbsPct = Math.max(...holdings.map(h => Math.abs(h.gainPct)), 1);

  const viewBoxWidth = 400;
  const viewBoxHeight = 180;
  const padding = { top: 16, bottom: 30, left: 10, right: 10 };
  const chartWidth = viewBoxWidth - padding.left - padding.right;
  const chartHeight = viewBoxHeight - padding.top - padding.bottom;
  const midY = padding.top + chartHeight / 2;
  const barGap = 2;
  const barWidth = Math.min((chartWidth - barGap * (holdings.length - 1)) / holdings.length, 40);
  const totalBarsWidth = holdings.length * barWidth + (holdings.length - 1) * barGap;
  const startX = padding.left + (chartWidth - totalBarsWidth) / 2;

  return (
    <div className="py-2">
      <svg viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} className="w-full max-h-[45vh]">
        <line x1={padding.left} y1={midY} x2={viewBoxWidth - padding.right} y2={midY}
          stroke={darkMode ? '#ffffff30' : '#00000020'} strokeWidth="0.5" />
        {holdings.map((h, i) => {
          const x = startX + i * (barWidth + barGap);
          const barMaxH = chartHeight / 2 - 4;
          const barH = (Math.abs(h.gainPct) / maxAbsPct) * barMaxH;
          const isPos = h.gainPct >= 0;
          const y = isPos ? midY - barH : midY;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barWidth} height={Math.max(barH, 1)} rx="1.5"
                fill={isPos ? '#22c55e' : '#ef4444'} opacity="0.85" />
              <text x={x + barWidth / 2} y={isPos ? y - 3 : y + barH + 6}
                textAnchor="middle" fontSize="5.5" fontWeight="bold" fill={isPos ? '#22c55e' : '#ef4444'}>
                {privacyMode ? '•••' : `${h.gainPct > 0 ? '+' : ''}${h.gainPct.toFixed(1)}%`}
              </text>
              <text x={x + barWidth / 2} y={viewBoxHeight - padding.bottom + 10}
                textAnchor="middle" fontSize="5" fill={darkMode ? '#9ca3af' : '#6b7280'}>
                {h.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Chart 6: Debt-to-Asset Ratio Trend
interface DebtRatioTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  snapshots: NetWorthSnapshot[];
  totalAssets: number;
  totalLiabilities: number;
}

const DebtRatioTab: React.FC<DebtRatioTabProps> = ({ darkMode, privacyMode, snapshots, totalAssets, totalLiabilities }) => {
  const currentRatio = totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : 0;
  const ratioColor = currentRatio > 50 ? 'text-red-500' : currentRatio > 30 ? 'text-yellow-500' : 'text-green-500';

  if (snapshots.length < 2) {
    return (
      <div className="space-y-2 py-2 text-center">
        <div className={`text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Debt-to-Asset Ratio</div>
        <div className={`text-3xl font-bold ${ratioColor}`}>{privacyMode ? '•••' : `${currentRatio.toFixed(1)}%`}</div>
        <p className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>Need at least 2 snapshots for trend chart</p>
      </div>
    );
  }

  const ratios = snapshots.map(s => s.totalAssets > 0 ? (s.totalLiabilities / s.totalAssets) * 100 : 0);
  const bestRatio = Math.min(...ratios);
  const worstRatio = Math.max(...ratios);

  const viewBoxWidth = 400;
  const viewBoxHeight = 200;
  const padding = 30;
  const chartWidth = viewBoxWidth - padding * 2;
  const chartHeight = viewBoxHeight - padding * 2;

  const maxR = Math.max(...ratios, 50);
  const minR = Math.min(...ratios, 0);
  const range = maxR - minR || 1;

  const linePoints = snapshots.map((_, i) => {
    const x = padding + (i / (snapshots.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((ratios[i] - minR) / range) * chartHeight;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  const thresholdY = padding + chartHeight - ((50 - minR) / range) * chartHeight;
  const showThreshold = 50 >= minR && 50 <= maxR;
  const firstDate = snapshots[0].date;
  const lastDate = snapshots[snapshots.length - 1].date;

  return (
    <div className="space-y-4 py-2">
      {/* Hero */}
      <div className="text-center">
        <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Debt-to-Asset Ratio</div>
        <div className={`text-3xl font-bold ${ratioColor}`}>{privacyMode ? '•••' : `${currentRatio.toFixed(1)}%`}</div>
      </div>

      <div className="flex items-center justify-center gap-6">
        <div className="text-center">
          <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Best</div>
          <div className="text-lg font-bold text-green-500">{privacyMode ? '•••' : `${bestRatio.toFixed(1)}%`}</div>
        </div>
        <div className="text-center">
          <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Worst</div>
          <div className="text-lg font-bold text-red-500">{privacyMode ? '•••' : `${worstRatio.toFixed(1)}%`}</div>
        </div>
      </div>

      <svg viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} className="w-full max-h-[35vh]">
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line key={i} x1={padding} y1={padding + chartHeight * pct} x2={viewBoxWidth - padding} y2={padding + chartHeight * pct}
            stroke={darkMode ? '#ffffff15' : '#00000010'} strokeWidth="0.5" />
        ))}
        {showThreshold && (
          <line x1={padding} y1={thresholdY} x2={viewBoxWidth - padding} y2={thresholdY}
            stroke="#ef4444" strokeWidth="0.8" strokeDasharray="4 3" opacity="0.5" />
        )}
        {showThreshold && (
          <text x={viewBoxWidth - padding + 2} y={thresholdY + 3} fill="#ef4444" fontSize="5" opacity="0.7">50%</text>
        )}
        <polyline points={linePoints} fill="none" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <text x={padding} y={viewBoxHeight - 5} fill={darkMode ? '#9ca3af' : '#6b7280'} fontSize="6" textAnchor="start">{firstDate}</text>
        <text x={viewBoxWidth - padding} y={viewBoxHeight - 5} fill={darkMode ? '#9ca3af' : '#6b7280'} fontSize="6" textAnchor="end">{lastDate}</text>
      </svg>
    </div>
  );
};

// Chart 7: 401k vs Brokerage Comparison
interface ComparisonTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  investments: Investment[];
  retirement401k: Retirement401k[];
  totalInvestmentValue: number;
  total401k: number;
  totalCrypto: number;
  cryptoHoldings: CryptoHolding[];
}

const ComparisonTab: React.FC<ComparisonTabProps> = ({ darkMode, privacyMode, investments, retirement401k, totalInvestmentValue, total401k, totalCrypto, cryptoHoldings }) => {
  const totalCombined = totalInvestmentValue + total401k + totalCrypto;
  const brokeragePct = totalCombined > 0 ? (totalInvestmentValue / totalCombined) * 100 : 33;
  const retirementPct = totalCombined > 0 ? (total401k / totalCombined) * 100 : 33;
  const cryptoPct = totalCombined > 0 ? (totalCrypto / totalCombined) * 100 : 34;


  return (
    <div className="space-y-5 py-2">
      {/* Side by side values */}
      <div className="flex items-center justify-center gap-4">
        <div className="text-center">
          <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Brokerage</div>
          <div className={`text-lg font-bold text-blue-500`}>{formatCurrency(totalInvestmentValue, privacyMode)}</div>
          <div className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>{investments.length} holdings</div>
        </div>
        <div className={`text-lg ${darkMode ? 'text-slate-600' : 'text-slate-300'}`}>/</div>
        <div className="text-center">
          <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>401k</div>
          <div className={`text-lg font-bold text-violet-500`}>{formatCurrency(total401k, privacyMode)}</div>
          <div className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>{retirement401k.length} holdings</div>
        </div>
        {totalCrypto > 0 && (
          <>
            <div className={`text-lg ${darkMode ? 'text-slate-600' : 'text-slate-300'}`}>/</div>
            <div className="text-center">
              <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Crypto</div>
              <div className={`text-lg font-bold text-amber-500`}>{formatCurrency(totalCrypto, privacyMode)}</div>
              <div className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>{cryptoHoldings.length} coins</div>
            </div>
          </>
        )}
      </div>

      {/* Split bar */}
      <div>
        <div className="flex h-2 rounded-full overflow-hidden">
          <div className="bg-blue-500" style={{ width: `${brokeragePct}%` }} />
          <div className="bg-violet-500" style={{ width: `${retirementPct}%` }} />
          {totalCrypto > 0 && <div className="bg-amber-500" style={{ width: `${cryptoPct}%` }} />}
        </div>
        <div className="flex justify-between mt-1">
          <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{privacyMode ? '•••' : `${brokeragePct.toFixed(0)}%`}</span>
          {totalCrypto > 0 && <span className={`text-xs ${darkMode ? 'text-amber-500/70' : 'text-amber-500'}`}>{privacyMode ? '•••' : `${cryptoPct.toFixed(0)}%`}</span>}
          <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{privacyMode ? '•••' : `${retirementPct.toFixed(0)}%`}</span>
        </div>
      </div>

    </div>
  );
};

// Chart 8: Budget Burn-Down
interface BurnDownTabProps {
  darkMode: boolean;
  privacyMode: boolean;
  budgetUtilization: Array<{ category: string; budgeted: number; spent: number; percentage: number; remaining: number; color: string }>;
  totalBudgeted: number;
  totalSpent: number;
}

const BurnDownTab: React.FC<BurnDownTabProps> = ({ darkMode, privacyMode, budgetUtilization, totalBudgeted, totalSpent }) => {
  if (budgetUtilization.length === 0) {
    return (
      <div className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        <p className="text-sm">No budget categories</p>
        <p className="text-xs mt-1">Add budget categories to see burn-down</p>
      </div>
    );
  }

  const sorted = [...budgetUtilization].sort((a, b) => b.percentage - a.percentage);
  const totalRemaining = totalBudgeted - totalSpent;

  return (
    <div className="space-y-4 py-2">
      <div className="text-center">
        <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Remaining Budget</div>
        <div className={`text-3xl font-bold ${totalRemaining >= 0 ? 'text-green-500' : 'text-red-500'}`}>
          {formatCurrency(totalRemaining, privacyMode)}
        </div>
        <div className={`text-xs mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          {formatCurrency(totalSpent, privacyMode)} of {formatCurrency(totalBudgeted, privacyMode)} spent
        </div>
      </div>

      <div className={`border-t ${darkMode ? 'border-white/10' : 'border-slate-100'} pt-3 space-y-3`}>
        {sorted.map((cat, idx) => {
          const isOver = cat.spent > cat.budgeted;
          const barPct = Math.min(cat.percentage, 100);
          return (
            <div key={idx}>
              <div className="flex justify-between items-center mb-1">
                <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{cat.category}</span>
                <span className={`text-sm font-semibold ${isOver ? 'text-red-500' : darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {privacyMode ? '•••' : `${Math.round(cat.percentage)}%`}
                </span>
              </div>
              <div className={`h-1.5 rounded-full ${darkMode ? 'bg-white/10' : 'bg-slate-100'}`}>
                <div
                  className={`h-full rounded-full transition-all ${isOver ? 'bg-red-500' : cat.percentage > 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                  style={{ width: `${barPct}%` }}
                />
              </div>
              <div className="flex justify-between mt-0.5">
                <span className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>{formatCurrency(cat.spent, privacyMode)}</span>
                <span className={`text-xs ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>{formatCurrency(cat.budgeted, privacyMode)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
